import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Card, CardHeader, CardTitle, CardBody, Table,
  TabContent, TabPane,
  Button, Badge, Input, 
  ButtonGroup, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Dropdown,
} from 'reactstrap';
// import classnames from 'classnames';
import { Line } from 'react-chartjs-2';
import numberWithCommas from "../constant/numberFormat.js";
import TinyLoading from "components/tiny/TinyLoading";

// Utility function to generate consistent colors based on an ID
const getColor = (id, alpha = 1) => {
  // Simple hash function to generate a numeric hash from a string
  const hash = Array.from(String(id)).reduce(
    (hash, char) => ((hash << 5) - hash) + char.charCodeAt(0), 0
  ) >>> 0;
  
  // Generate HSL color with high saturation and light-medium lightness for good visibility
  const h = hash % 360; // hue: 0-359
  const s = 70 + (hash % 20); // saturation: 70-89%
  const l = 45 + (hash % 15); // lightness: 45-59%
  
  return alpha < 1 
    ? `hsla(${h}, ${s}%, ${l}%, ${alpha})`
    : `hsl(${h}, ${s}%, ${l}%)`;
};

// Utility function to format date strings
const formatDate = (dateString) => {
  // Handle common date formats
  if (!dateString) return '';
  
  // Helper function to pad with leading zeros
  const padZero = (num) => num.toString().padStart(2, '0');
  
  // Handle period format like "20230501-20230507"
  if (dateString.includes('-') && dateString.length === 17) {
    const start = dateString.substring(0, 8);
    const end = dateString.substring(9);
    
    const startDate = new Date(
      parseInt(start.substring(0, 4)),
      parseInt(start.substring(4, 6)) - 1,
      parseInt(start.substring(6, 8))
    );
    
    const endDate = new Date(
      parseInt(end.substring(0, 4)),
      parseInt(end.substring(4, 6)) - 1,
      parseInt(end.substring(6, 8))
    );
    
    // Format with leading zeros for month and day
    const formatDateWithZeros = (date) => {
      return `${date.getFullYear()}/${padZero(date.getMonth() + 1)}/${padZero(date.getDate())}`;
    };
    
    return `${formatDateWithZeros(startDate)} - ${formatDateWithZeros(endDate)}`;
  }
  
  // Handle ISO date strings
  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return `${date.getFullYear()}/${padZero(date.getMonth() + 1)}/${padZero(date.getDate())}`;
    }
  } catch (e) {
    console.error('Error formatting date:', e);
  }
  
  // Default: return the original string if we couldn't parse it
  return dateString;
};

const WeeklyTotalWin = (props) => {
  // CSS for week selector
  const styleSheet = document.createElement("style");
  styleSheet.innerText = `
    .week-selector-container {
      margin-bottom: 12px; // Reduced from 15px
      width: 100%;
    }
    .week-picker-header {
      margin-bottom: 8px; // Reduced from 10px
    }
    .week-picker-controls {
      display: flex;
      align-items: center;
    }
    .week-navigation-buttons {
      display: flex;
    }
    
    .week-item {
      min-width: 110px;
      position: relative;
      overflow: hidden;
      padding: 4px 2px; // Reduced vertical padding from 6px
      min-height: 38px; // Added to ensure consistent height
    }
    
    .week-item:hover {
      transform: translateY(-1px);
      box-shadow: 0 3px 6px rgba(0,0,0,0.2) !important;
      border-color: #666;
      z-index: 1;
    }
    
    .week-item.selected {
      background-color: #4CAF50 !important;
      border-color: #81C784 !important;
    }
    
    .weeks-grid {
      position: relative;
      background: linear-gradient(180deg, #2A2A2A 0%, #252525 100%);
    }
    
    .weeks-grid::-webkit-scrollbar {
      width: 6px;
    }
    
    .weeks-grid::-webkit-scrollbar-track {
      background: #1A1A1A;
      border-radius: 3px;
    }
    
    .weeks-grid::-webkit-scrollbar-thumb {
      background: #4CAF50;
      border-radius: 3px;
      border: 1px solid #1A1A1A;
    }
    
    .weeks-grid::-webkit-scrollbar-thumb:hover {
      background: #45a049;
    }
    
    .custom-switch:hover {
      opacity: 0.9;
    }
    
    @media (max-width: 1600px) {
      .week-item {
        min-width: 105px;
      }
    }
    
    @media (max-width: 1400px) {
      .week-item {
        min-width: 100px;
      }
    }
    
    @media (max-width: 1200px) {
      .week-item {
        min-width: 95px;
      }
    }
    
    @media (max-width: 992px) {
      .week-item {
        min-width: 90px;
      }
    }
    
    @media (max-width: 768px) {
      .week-item {
        min-width: 85px;
      }
    }
  `;
  document.head.appendChild(styleSheet);

  // Destructure props at the top
  const {
    onLoad,
    loadPeriodData,
    loadPeriodPlayerData,
    loadMultiPeriodPlayerData,
    loadChartData,
    loadMultiPeriodData,
    weekly_total_win,
    error,
    union_info,
    periods: usePeriods,
    currentPeriodIndex: useCurrentPeriodIndex,
    loading,
    chartData,
    clubData,
    agentData,
    subagentData,
    weekly_total_win_sum,
    weekly_total_player_win
  } = props;

  // UI and data state
  const [state, setState] = useState({
    viewMode: 'table', // table or chart
    paymentType: 'all', // all, prepaid, credit
    timeRange: 'week', // week, month, quarter, year
    chartMetric: 'fee', // fee, win, leaderboard
    hierarchyLevel: 'union', // union, club, agent, subagent, player
    currentUnionId: null,
    currentClubId: null,
    currentAgentId: null,
    // Navigation history for breadcrumbs and back button
    navigationHistory: [],
    sortField: 'default',
    sortDirection: 'desc',
    isFromSubagent: false,
    superAgentId: null,
    showPlayerTable: false,
    searchText: '',
    searchResults: [],
    showSearchResults: false,
    highlightEntity: null,
    // Period selection mode state
    periodSelectionMode: 'single', // single or multi
    isPeriodSelectionActive: false, // Is user currently in selection mode
    selectedStartPeriod: null, // Start index of selection
    selectedEndPeriod: null, // End index of selection
    // Dropdown visibility states
    paymentTypeDropdownOpen: false,
    timeRangeDropdownOpen: false,
    chartMetricDropdownOpen: false,
    periodSelectorDropdownOpen: false,
    // Tax ratio filter
    taxRatio: '',
    taxFilterType: 'withLeaderboard',
    // Sort and pagination state for player table
    playerSortBy: 'fee',
    playerSortOrder: -1,
    currentPage: 1,
    pageSize: 50,
    // Search state
    searchLevel: 'union'
  });

  // Use real data from props
  const useWeeklyTotalWin = weekly_total_win || [];
  const useClubData = clubData || {};
  const useAgentData = agentData || {};
  const useSubagentData = subagentData || {};
  const useChartData = chartData || { labels: [], metrics: {} };

  // Initial data loading - fetch latest week's union data
  useEffect(() => {
    if (onLoad) {
      onLoad();
      //
      setState(prev => ({
        ...prev,
        selectedStartPeriod: null,
        selectedEndPeriod: null,
        isSelectingEndWeek: false,
        // Reset hierarchy level to union
        hierarchyLevel: 'union',
        currentUnionId: null,
        currentClubId: null,
        currentAgentId: null,
        navigationHistory: [],
        searchQuery: '',
        // Reset search state when component mounts (user returns to page)
        searchResults: null,
        showSearchResults: false,
        searchedPath: null,
        highlightEntity: null
      }));
    }
  }, []);
  
  // Helper function to build hierarchy path
  const buildHierarchyPath = (level, unionId = null, clubId = null, superAgentId = null, agentId = null) => {
    // Always use the provided level parameter, not state.hierarchyLevel
    return {
      hierarchyLevel: level,
      level: level,
      unionId: unionId || state.currentUnionId,
      clubId: clubId || state.currentClubId,
      superAgentId: superAgentId !== null ? superAgentId : state.superAgentId,
      agentId: agentId !== null ? agentId : state.currentAgentId
    };
  };

  // Helper function to build hierarchy path for player mode, handles super agent direct clients correctly
  const buildPlayerHierarchyPath = () => {
    // If we're in player table mode, always construct player hierarchy
    if (state.showPlayerTable) {
      // Use the same logic as togglePlayerTableMode to determine the correct hierarchy
      if (state.hierarchyLevel === 'agent') {
        // 在俱乐部层级（代理列表）切换到玩家表格 = 俱乐部直客
        return {
          hierarchyLevel: 'player',
          level: 'player',
          unionId: state.currentUnionId,
          clubId: state.currentClubId,
          superAgentId: "",  // 俱乐部直客：空字符串
          agentId: ""        // 俱乐部直客：空字符串
        };
      } else if (state.hierarchyLevel === 'subagent') {
        // 在超代层级（子代理列表）切换到玩家表格 = 超代直客
        // 注意：由于setState是异步的，state.superAgentId可能还没更新，所以使用currentAgentId
        const superAgentIdToUse = state.superAgentId || state.currentAgentId;
        return {
          hierarchyLevel: 'player',
          level: 'player',
          unionId: state.currentUnionId,
          clubId: state.currentClubId,
          superAgentId: superAgentIdToUse,  // 使用正确的超代ID
          agentId: ""                       // 超代直客：空字符串
        };
      } else {
        // 其他情况使用默认逻辑，但要确保传递正确的超代ID
        return buildHierarchyPath('player', state.currentUnionId, state.currentClubId, state.superAgentId, state.currentAgentId);
      }
    } else {
      // Not in player table mode, use normal hierarchy path construction
      return buildHierarchyPath(state.hierarchyLevel);
    }
  };

  // Helper function to load period data with hierarchy path
  const loadPeriodDataWithPath = (periodIndex, hierarchyPath) => {
    // Ensure consistent parameter names
    const normalizedPath = {
      ...hierarchyPath,
      clubID: hierarchyPath.clubId,  // Add clubID for single period mode
      clubId: hierarchyPath.clubId,  // Keep clubId for multi period mode
      superAgentId: hierarchyPath.superAgentId  // Ensure superAgentId is included
    };

    if (state.periodSelectionMode === 'multi' && state.selectedStartPeriod !== null && state.selectedEndPeriod !== null) {
      // In multi-select mode, use loadMultiPeriodData with the selected range
      const periodRange = [state.selectedStartPeriod, state.selectedEndPeriod];
      if (loadMultiPeriodData) {
        loadMultiPeriodData(periodRange, normalizedPath);
      }
    } else {
      // In single mode, use regular loadPeriodData
      if (loadPeriodData) {
        loadPeriodData(periodIndex, normalizedPath);
      }
    }
  };

  // 新增：Helper function to load player period data with hierarchy path
  const loadPeriodPlayerDataWithPath = (periodIndex, hierarchyPath) => {
    // Ensure consistent parameter names
    const normalizedPath = {
      ...hierarchyPath,
      clubID: hierarchyPath.clubId,  // Add clubID for single period mode
      clubId: hierarchyPath.clubId   // Keep clubId for multi period mode
    };

    if (state.periodSelectionMode === 'multi' && state.selectedStartPeriod !== null && state.selectedEndPeriod !== null) {
      // In multi-select mode, use loadMultiPeriodPlayerData with the selected range
      const periodRange = [state.selectedStartPeriod, state.selectedEndPeriod];
      if (loadMultiPeriodPlayerData) {
        loadMultiPeriodPlayerData(
          periodRange, 
          normalizedPath, 
          state.playerSortBy, 
          state.playerSortOrder, 
          state.currentPage, 
          state.pageSize
        );
      }
    } else {
      // In single mode, use loadPeriodPlayerData instead of loadPeriodData
      if (loadPeriodPlayerData) {
        loadPeriodPlayerData(
          periodIndex, 
          normalizedPath, 
          state.playerSortBy, 
          state.playerSortOrder, 
          state.currentPage, 
          state.pageSize
        );
      }
    }
  };

  // Load data for a specific period
  const loadPeriodDataHandler = (index) => {
    // Use the current hierarchy level from state
    const hierarchyPath = buildPlayerHierarchyPath();
    
    // Check if we're in player table mode to call the appropriate API
    if (state.showPlayerTable) {
      loadPeriodPlayerDataWithPath(parseInt(index), hierarchyPath);
    } else {
      loadPeriodDataWithPath(parseInt(index), hierarchyPath);
    }
  };

  // Load clubs for a specific union
  const loadClubsForUnionHandler = (unionId) => {
    
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'club',  // Add hierarchyLevel here
      level: 'club',
      unionId: unionId,
      clubId: null,
      agentId: null
    };
    
    // Update state and load period data
    setState(prev => {
      return {
        ...prev,
        hierarchyLevel: 'club',
        currentUnionId: unionId,
        currentClubId: null,
        currentAgentId: null,
        superAgentId: null,  // 清理超代ID，避免跨联盟时的状态污染
        navigationHistory: [...prev.navigationHistory, { level: 'club', id: unionId }]
      };
    });
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };
  
  // Load agents for a specific club
  const loadAgentsForClubHandler = (clubId) => {
    
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'agent',
      level: 'agent',
      unionId: state.currentUnionId,
      clubId: clubId,
      agentId: null
    };
    
    
    // Update state and load period data
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'agent',
      currentClubId: clubId,
      currentAgentId: null,
      superAgentId: null,  // 清理超代ID，避免跨俱乐部时的状态污染
      navigationHistory: [...prev.navigationHistory, { level: 'agent', id: clubId }]
    }));
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };
  
  // Load subagents for a specific agent
  const loadSubagentsForAgentHandler = (agentId) => {
    // Check if we already have this data
    if (useSubagentData && useSubagentData[agentId]) {
      navigateToSubAgents(agentId);
      return;
    }
    
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'subagent',  // Add hierarchyLevel here
      level: 'subagent',
      unionId: state.currentUnionId,
      clubId: state.currentClubId,
      superAgentId: agentId,  // 超代ID设为当前代理ID
      agentId: agentId  // 在subagent层级，agentId应该是超代ID，用于查询该超代下的子代理
    };
    
    // Update state and load period data
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'subagent',
      superAgentId: agentId,     // 设置超代ID
      currentAgentId: agentId,   // 保持当前代理ID为超代ID，以便后续使用
      navigationHistory: [...prev.navigationHistory, { level: 'subagent', id: agentId }]
    }));
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  // 新增：加载Normal Agent的玩家数据
  const loadPlayersForAgentHandler = (agentId) => {
    // Build the hierarchy path with hierarchyLevel
    const playerHierarchyPath = {
      hierarchyLevel: 'player',  // 新的层级：player
      level: 'player',
      unionId: state.currentUnionId,
      clubId: state.currentClubId,
      agentId: agentId,
      // 处理超代情况：如果是从subagent进入，使用superAgentId
      // 注意：由于setState是异步的，使用與togglePlayerTableMode相同的邏輯
      superAgentId: state.hierarchyLevel === 'subagent' ? (state.superAgentId || state.currentAgentId) : null
    };
    
    // Update state and load period data
    const superAgentIdToKeep = state.hierarchyLevel === 'subagent' ? (state.superAgentId || state.currentAgentId) : null;
    
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'player',  // 设置新的层级
      showPlayerTable: true,     // 确保显示玩家表格
      currentAgentId: agentId,
      // 记录是否来自子代理页面以及超级代理ID
      isFromSubagent: state.hierarchyLevel === 'subagent',
      // 保持现有的superAgentId（如果从subagent来则不变，否则为null）
      superAgentId: superAgentIdToKeep,
      navigationHistory: [...prev.navigationHistory, { 
        level: 'player', 
        id: agentId,
        // 记录额外信息
        isFromSubagent: state.hierarchyLevel === 'subagent',
        superAgentId: superAgentIdToKeep
      }],
      // 重置分页和排序状态
      currentPage: 1,
      playerSortBy: 'fee',
      playerSortOrder: -1,
      // 设置为按总服务费降序排序
      sortField: 'serviceFee',
      sortDirection: 'desc'
    }));
    
    // 根據當前模式調用對應的 API
    if (state.periodSelectionMode === 'multi' && state.selectedStartPeriod !== null && state.selectedEndPeriod !== null) {
      // 多周模式：使用 loadMultiPeriodPlayerData
      const periodRange = [state.selectedStartPeriod, state.selectedEndPeriod];
      if (loadMultiPeriodPlayerData) {
        loadMultiPeriodPlayerData(
          periodRange, 
          playerHierarchyPath, 
          'fee',  // 默认按服务费排序
          -1,     // 默认降序
          1,      // 默认第一页
          state.pageSize
        );
      }
    } else {
      // 單周模式：使用 loadPeriodPlayerData
      const periodIndexToUse = props.currentPeriodIndex !== undefined ? props.currentPeriodIndex : useCurrentPeriodIndex;
      if (loadPeriodPlayerData) {
        loadPeriodPlayerData(
          periodIndexToUse, 
          playerHierarchyPath, 
          'fee',  // 默认按服务费排序
          -1,     // 默认降序
          1,      // 默认第一页
          state.pageSize
        );
      }
    }
  };

  // Navigation functions
  const navigateToClubs = (unionId) => {
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'club',  // Add hierarchyLevel here
      level: 'club',
      unionId: unionId,
      clubId: null,
      agentId: null
    };
    
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'club',
      currentUnionId: unionId,
      currentClubId: null,
      currentAgentId: null,
      navigationHistory: [...prev.navigationHistory, { level: 'club', id: unionId }]
    }));
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  const navigateToAgents = (clubId) => {
    
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'agent',
      level: 'agent',
      unionId: state.currentUnionId,
      clubId: clubId,
      agentId: null
    };
    
    
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'agent',
      currentClubId: clubId,
      currentAgentId: null,
      navigationHistory: [...prev.navigationHistory, { level: 'agent', id: clubId }]
    }));
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  const navigateToSubAgents = (agentId) => {
    // Build the hierarchy path with hierarchyLevel
    const hierarchyPath = {
      hierarchyLevel: 'subagent',  // Add hierarchyLevel here
      level: 'subagent',
      unionId: state.currentUnionId,
      clubId: state.currentClubId,
      superAgentId: agentId,  // 超代ID设为当前代理ID
      agentId: agentId  // 在subagent层级，agentId应该是超代ID，用于查询该超代下的子代理
    };
    
    setState(prev => ({
      ...prev,
      hierarchyLevel: 'subagent',
      superAgentId: agentId,     // 设置超代ID
      currentAgentId: agentId,   // 保持当前代理ID为超代ID，以便后续使用
      navigationHistory: [...prev.navigationHistory, { level: 'subagent', id: agentId }]
    }));
    
    // Use the hierarchy path we built before state update
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  // Navigate back to previous level
  const navigateBack = () => {
    if (state.navigationHistory.length === 0) return;
    
    // Get the previous item and remove the last item from history
    const newHistory = [...state.navigationHistory];
    newHistory.pop();
    
    // If no more items in history, return to union level
    if (newHistory.length === 0) {
      setState(prevState => ({
        ...prevState,
        hierarchyLevel: 'union',
        currentUnionId: null,
        currentClubId: null,
        currentAgentId: null,
        showPlayerTable: false,
        navigationHistory: [],
        // 重置分页和排序状态
        currentPage: 1,
        playerSortBy: 'fee',
        playerSortOrder: -1
      }));
      
      // Load union data for the current period
      const hierarchyPath = buildHierarchyPath('union');
      loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
      
      return;
    }
    
    // Find the last remaining item to determine where to go back to
    const lastItem = newHistory[newHistory.length - 1];
    const level = lastItem.level;
    const id = lastItem.id;
    
    // Set the new state based on the previous level
    let newState = {
      navigationHistory: newHistory,
      // 重置分页和排序状态如果返回到非玩家视图
      currentPage: 1,
      playerSortBy: 'fee',
      playerSortOrder: -1
    };
    
    switch(level) {
      case 'club':
        newState = {
          ...newState,
          hierarchyLevel: 'club',
          currentUnionId: id,
          currentClubId: null,
          currentAgentId: null,
          showPlayerTable: false,
        };
        break;
        
      case 'agent':
        newState = {
          ...newState,
          hierarchyLevel: 'agent',
          currentClubId: id,
          currentAgentId: null,
          showPlayerTable: false,
        };
        break;
      
      case 'subagent':
        newState = {
          ...newState,
          hierarchyLevel: 'subagent',
          superAgentId: id,  // subagent level的ID是超代ID
          currentAgentId: null,  // 重置当前代理ID
          showPlayerTable: false,
        };
        break;
        
      case 'player':
        // 从玩家返回到代理时，保留层级但关闭玩家表格
        newState = {
          ...newState,
          hierarchyLevel: lastItem.isFromSubagent ? 'subagent' : 'agent',
          currentAgentId: id,
          superAgentId: lastItem.superAgentId,
          isFromSubagent: lastItem.isFromSubagent,
          showPlayerTable: false,
        };
        break;
        
      default:
        newState = {
          ...newState,
          hierarchyLevel: 'union',
          currentUnionId: null,
          currentClubId: null,
          currentAgentId: null,
          showPlayerTable: false,
        };
    }
    
    setState(prevState => ({
      ...prevState,
      ...newState
    }));
    
    // 构建层级路径并加载数据
    const hierarchyPath = buildHierarchyPath(newState.hierarchyLevel);
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  const navigateToPeriod = (offset) => {
    const newIndex = useCurrentPeriodIndex + offset;
    if (newIndex >= 0 && newIndex < usePeriods.length) {
      // Build the hierarchy path
      const hierarchyPath = buildPlayerHierarchyPath();
      
      // Check if we're in player table mode to call the appropriate API
      if (state.showPlayerTable) {
        loadPeriodPlayerDataWithPath(parseInt(newIndex), hierarchyPath);
      } else {
        loadPeriodDataWithPath(parseInt(newIndex), hierarchyPath);
      }
    }
  };

  // Load chart data based on current hierarchy level and time range
  const loadChartDataHandler = () => {
    if (loadChartData) {
      // Create a hierarchy path matching the current view
      const hierarchyPath = {
        hierarchyLevel: state.hierarchyLevel,
        level: state.hierarchyLevel,
        unionId: state.currentUnionId,
        clubId: state.currentClubId,
        agentId: state.currentAgentId
      };
      
      
      // Pass the hierarchy path and time range to backend
      // The metric will be filtered on the frontend
      loadChartData(state.timeRange, hierarchyPath);
    }
  };

  // Updated sort handling function
  const handleSort = (field) => {
    let newSortField = field;
    let newSortOrder = -1;
    let newSortBy = "fee"; // 默认排序字段

    // 判断点击了哪个字段并转换为后端需要的排序字段
    if (field === 'serviceFee') {
      newSortBy = "fee";
    } else if (field === 'totalWin') {
      newSortBy = "total_win";
    } else if (field === 'leaderboard') {
      newSortBy = "leaderboard";
    }

    // 如果已经按这个字段排序了，切换排序方向
    if (state.sortField === field) {
      newSortOrder = state.playerSortOrder === -1 ? 1 : -1;
    }

    // 更新状态并重新加载数据
    setState(prevState => ({
      ...prevState,
      sortField: newSortField,
      sortDirection: newSortOrder === -1 ? 'desc' : 'asc',
      playerSortBy: newSortBy,
      playerSortOrder: newSortOrder,
      currentPage: 1 // 重置页码
    }));

    // 如果是玩家表格，需要重新加载数据
    if (state.showPlayerTable || state.hierarchyLevel === 'player') {
      const periodIndexToUse = props.currentPeriodIndex !== undefined ? props.currentPeriodIndex : useCurrentPeriodIndex;
      const hierarchyPath = buildHierarchyPath(state.hierarchyLevel);
      
      // 重新加载玩家数据
      if (loadPeriodPlayerData) {
        loadPeriodPlayerData(
          periodIndexToUse, 
          hierarchyPath, 
          newSortBy,
          newSortOrder,
          1, // 重置为第一页
          state.pageSize
        );
      }
    }
  };

  // Updated sorting function for all entity types
  const sortEntities = (entities) => {
    if (!entities || entities.length === 0 || state.sortField === 'default') return entities;
    
    return [...entities].sort((a, b) => {
      let valueA, valueB;
      
      // Extract the correct property based on the sort field
      switch (state.sortField) {
        case 'serviceFee':
          valueA = parseFloat(a.totalServiceFee || 0);
          valueB = parseFloat(b.totalServiceFee || 0);
              break;
        case 'totalWin':
          valueA = parseFloat(a.totalWin || 0);
          valueB = parseFloat(b.totalWin || 0);
          break;
        case 'leaderboard':
          valueA = parseFloat(a.leaderboardRank || 0);
          valueB = parseFloat(b.leaderboardRank || 0);
          break;
        case 'delivery':
          valueA = parseFloat(a.totalDelivery || 0);
          valueB = parseFloat(b.totalDelivery || 0);
          break;
        case 'agentType':
          // For agent type, sort alphabetically
          valueA = a.agentType || '';
          valueB = b.agentType || '';
          // Use string comparison for text
          if (state.sortDirection === 'asc') {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
          break;
        default:
          return 0;
      }
      
      // Compare based on sort direction (for numeric values)
      if (state.sortField !== 'agentType') {
        if (state.sortDirection === 'asc') {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      }
      return 0; // Should never reach here because of the early return in agentType case
    });
  };

  /**
   * Filter entities based on payment type (prepaid/credit)
   * Uses union_info data to determine payment type
   */
  const filterByPaymentType = (entities, entityType) => {
    if (state.paymentType === 'all') return entities;
    
    if (!union_info || union_info.length === 0) return entities;
    
    if (entityType === 'union') {
      // For unions, use storeValue directly from the entity data
      return entities.filter(entity => {
        // Use storeValue field directly from API response
        const storeValue = entity.storeValue || false;
        
        // 'prepaid' = true, 'credit' = false for storeValue
        return (state.paymentType === 'prepaid' && storeValue) || 
               (state.paymentType === 'credit' && !storeValue);
      });
    } else if (entityType === 'club') {
      // For clubs, use storeValue directly from the entity data
      return entities.filter(club => {
        // Use storeValue field directly from API response
        const storeValue = club.storeValue || false;
        
        // Filter based on club's storeValue property
        return (state.paymentType === 'prepaid' && storeValue) || 
               (state.paymentType === 'credit' && !storeValue);
      });
    } else if (entityType === 'agent') {
      // For agents, use storeValue directly from the entity data
      return entities.filter(agent => {
        // Use storeValue field directly from API response
        const storeValue = agent.storeValue || false;
        
        return (state.paymentType === 'prepaid' && storeValue) || 
               (state.paymentType === 'credit' && !storeValue);
      });
    } else if (entityType === 'subagent') {
      // For subagents, use storeValue directly from the entity data
      return entities.filter(subagent => {
        // Use storeValue field directly from API response
        const storeValue = subagent.storeValue || false;
        
        return (state.paymentType === 'prepaid' && storeValue) || 
               (state.paymentType === 'credit' && !storeValue);
      });
    }
    
    return entities; // Default fallback
  };

  // Add new filter function for tax ratio
  const filterByTaxRatio = (entities) => {
    if (!state.taxRatio) return entities;
    
    const ratio = parseFloat(state.taxRatio);
    if (isNaN(ratio)) return entities;
    
    return entities.filter(entity => {
      const totalServiceFee = entity.totalServiceFee || 0;
      if (totalServiceFee === 0) return false;
      
      const totalWin = entity.totalWin || 0;
      const leaderboardRank = entity.leaderboardRank || 0;
      
      if (state.taxFilterType === 'withLeaderboard') {
        return ((totalWin + leaderboardRank) / totalServiceFee) > ratio;
      } else {
        return (totalWin / totalServiceFee) > ratio;
      }
    });
  };

  // Add filter function to exclude entities that weren't active during selected periods
  const filterByJoinedWeek = (entities) => {
    // Only apply this filter in multi-period mode
    if (state.periodSelectionMode !== 'multi') return entities;
    
    const selectedPeriods = getSelectedPeriodRange();
    if (!selectedPeriods || selectedPeriods.length === 0) return entities;
    
    const latestSelectedPeriod = selectedPeriods[0]; // Periods are sorted newest first
    
    return entities.filter(entity => {
      // If entity has no joined_week_duration, assume it was always active
      if (!entity.joined_week_duration) return true;
      
      // Check if entity joined before or during the latest selected period
      // Only show entities that were active for at least part of the selected range
      return entity.joined_week_duration <= latestSelectedPeriod;
    });
  };

  // Helper function to get names from entity data - moved outside both functions to be reusable
  const findEntityName = (data, level, id) => {
    if (!data || !id) return '';
    
    let entity;
    
    switch(level) {
      case 'union':
        // Try to find union in top-level data first
        entity = data.find(item => item.unionId == id);
        if (entity) {
          return entity.unionName;
        }
        
        // If not found and we're in club level, try to find it in props.union_info
        if (props.union_info) {
          const unionInfo = props.union_info.find(union => union._id == id || union.unionID == id);
          if (unionInfo) {
            return unionInfo.unionName;
          }
        }
        
        return '';
        
      case 'club':
        // Try to find in current data
        entity = data.find(item => item.clubId == id);
        if (entity) {
          return entity.clubName;
        }
        
        // Try to find in union_info
        if (props.union_info && state.currentUnionId) {
          const union = props.union_info.find(u => u._id == state.currentUnionId || u.unionID == state.currentUnionId);
          if (union && union.clubs) {
            
            // Handle both array and object structures for clubs
            let clubsArray = [];
            if (Array.isArray(union.clubs)) {
              clubsArray = union.clubs;
            } else if (typeof union.clubs === 'object') {
              clubsArray = Object.values(union.clubs);
            }
            
            const club = clubsArray.find(c => c._id == id || c.clubID == id);
            if (club) {
              return club.clubName;
            }
          }
        }
        
        return '';
        
      case 'agent':
        entity = data.find(item => item.agentId == id);
        if (entity) {
          return entity.agentName;
        }
        
        // Try to find in union_info
        if (props.union_info && state.currentUnionId && state.currentClubId) {
          const union = props.union_info.find(u => u._id == state.currentUnionId || u.unionID == state.currentUnionId);
          if (union && union.clubs) {
            
            // Handle both array and object structures for clubs
            let clubsArray = [];
            if (Array.isArray(union.clubs)) {
              clubsArray = union.clubs;
            } else if (typeof union.clubs === 'object') {
              clubsArray = Object.values(union.clubs);
            }
            
            const club = clubsArray.find(c => c._id == state.currentClubId || c.clubID == state.currentClubId);
            if (club && club.agents) {
              
              // Handle both array and object structures for agents
              let agentsArray = [];
              if (Array.isArray(club.agents)) {
                agentsArray = club.agents;
              } else if (typeof club.agents === 'object') {
                agentsArray = Object.values(club.agents);
              }
              
              const agent = agentsArray.find(a => a._id == id || a.agentID == id);
              if (agent) {
                return agent.agentName;
              }
            }
          }
        }
        
        return '';
        
      case 'subagent':
        // 查找子代理名称
        entity = data.find(item => item.subagentId == id || item.subAgentId == id);
        if (entity) {
          return entity.subagentName || entity.subAgentName;
        }
        
        // 如果在当前数据中找不到，尝试从之前的代理层级数据中查找
        if (props.weekly_total_win) {
          entity = props.weekly_total_win.find(item => item.subagentId == id || item.subAgentId == id);
          if (entity) {
            return entity.subagentName || entity.subAgentName;
          }
        }
        
        return '';
        
      default:
        return '';
    }
  };

  // Add function to calculate actual joined weeks
  const calculateActualJoinedWeeks = (entity, selectedPeriods) => {
    if (!entity.joined_week_duration || !Array.isArray(selectedPeriods) || selectedPeriods.length === 0) {
      return null;
    }

    const joinedWeek = entity.joined_week_duration;
    const earliestSelectedPeriod = selectedPeriods[selectedPeriods.length - 1]; // Periods are sorted newest first
    
    // If joined week is after the earliest selected period, calculate actual weeks
    if (joinedWeek > earliestSelectedPeriod) {
      // Count how many selected periods are >= joined week
      const actualWeeks = selectedPeriods.filter(period => period >= joinedWeek).length;
      return actualWeeks;
    }
    
    return null; // Entity was present for all selected periods
  };

  // Add function to get period range for display
  const getSelectedPeriodRange = () => {
    if (!Array.isArray(usePeriods) || usePeriods.length === 0) return [];
    
    // For multi-period mode, use selected range
    if (state.periodSelectionMode === 'multi' && 
        state.selectedStartPeriod !== null && 
        state.selectedEndPeriod !== null) {
      const startIdx = Math.min(state.selectedStartPeriod, state.selectedEndPeriod);
      const endIdx = Math.max(state.selectedStartPeriod, state.selectedEndPeriod);
      
      if (startIdx < 0 || endIdx >= usePeriods.length) {
        return [];
      }
      
      return usePeriods.slice(startIdx, endIdx + 1);
    }
    
    // For single period mode, return current period
    if (props.currentPeriodIndex !== undefined && props.currentPeriodIndex < usePeriods.length) {
      return [usePeriods[props.currentPeriodIndex]];
    }
    
    return [];
  };

  // Render the entity table based on the current hierarchy level
  const renderEntityTable = () => {
    const selectedPeriods = getSelectedPeriodRange();
    
    let title = '';
    let dataToRender = [];
    
    const currentData = state.periodSelectionMode === 'multi' ? 
      (props.weekly_total_win_sum || []) : 
      (props.weekly_total_win || []);
    

    
    // Find the current union, club, agent names - using the same lookup as in breadcrumbs
    let unionName = '';
    let clubName = '';
    let agentName = '';
    
    if (state.currentUnionId) {
      unionName = findEntityName(currentData, 'union', state.currentUnionId);
    }
    
    if (state.currentClubId) {
      clubName = findEntityName(currentData, 'club', state.currentClubId);
    }
    
    if (state.currentAgentId) {
      agentName = findEntityName(currentData, 'agent', state.currentAgentId);
    }
    
    // Set title based on hierarchy level with names included
    switch(state.hierarchyLevel) {
      case 'union':
        title = '所有聯盟';
        dataToRender = sortEntities(filterByJoinedWeek(filterByTaxRatio(filterByPaymentType(currentData || [], 'union')))) || [];
        break;
        
      case 'club':
        title = unionName ? 
          `${unionName} (${state.currentUnionId}聯盟) 的俱樂部` : 
          `聯盟 ${state.currentUnionId} 的俱樂部`;
        dataToRender = sortEntities(filterByJoinedWeek(filterByTaxRatio(filterByPaymentType(currentData || [], 'club')))) || [];
        break;
        
      case 'agent':
        if (state.showPlayerTable) {
          title = clubName ? 
            `${clubName} (${state.currentClubId}俱樂部) 的直客玩家` : 
            `俱樂部 ${state.currentClubId} 的直客玩家`;
          // 使用真实玩家数据而不是模拟数据
          dataToRender = props.weekly_total_player_win || [];
        } else {
        title = clubName ? 
          `${clubName} (${state.currentClubId}俱樂部) 的代理` : 
          `俱樂部 ${state.currentClubId} 的代理`;
                  dataToRender = sortEntities(filterByJoinedWeek(filterByTaxRatio(filterByPaymentType(currentData || [], 'agent')))) || [];
        }
        break;
        
            case 'subagent':
        // 在subagent層級，需要獲取正確的超級代理名稱
        // 不能直接使用 agentName，因為它可能來自舊的 currentData
        const superAgentId = state.superAgentId || state.currentAgentId;
        
        // 嘗試多種方式獲取超級代理名稱
        let superAgentName = '';
        
        // 1. 首先從當前數據中查找
        if (superAgentId) {
          superAgentName = findEntityName(currentData, 'agent', superAgentId);
        }
        
        // 2. 如果沒找到，從代理層級數據中查找
        if (!superAgentName && props.weekly_total_win && superAgentId) {
          superAgentName = findEntityName(props.weekly_total_win, 'agent', superAgentId);
        }
        
        // 3. 如果還是沒找到，使用 agentName 作為 fallback
        if (!superAgentName) {
          superAgentName = agentName;
        }
        
                if (state.showPlayerTable) {
          // 在超代层级切换到玩家表格 = 显示超代直客
          title = superAgentName ? 
            `${superAgentName} (${superAgentId}超級代理) 的直客玩家` : 
            `超級代理 ${superAgentId} 的直客玩家`;
          // 使用玩家数据
          dataToRender = props.weekly_total_player_win || [];
        } else {
          // 显示超級代理的記錄（不是子代理列表，而是這個超級代理本身的記錄）
          // 注意：不要在title中包含「的單周記錄」，因為UI會自動添加「單周記錄」
          title = superAgentName ? 
            `${superAgentName} (${superAgentId}超級代理)` : 
            `超級代理 ${superAgentId}`;
          dataToRender = sortEntities(filterByJoinedWeek(filterByTaxRatio(filterByPaymentType(currentData || [], 'subagent')))) || [];
        }
        break;
        
      case 'player':
        // 区分普通代理和子代理的玩家
        const agentType = (() => {
          // 从导航历史中找到前一级的类型
          const prevLevel = state.navigationHistory.length > 1 ? 
            state.navigationHistory[state.navigationHistory.length - 2] : null;
          
          if (prevLevel && prevLevel.level === 'subagent') {
            return '子代理';
          } else {
            return '普通代理';
          }
        })();
        
        title = agentName ? 
          `${agentName} (${state.currentAgentId}${agentType}) 的玩家` : 
          `${agentType} ${state.currentAgentId} 的玩家`;
        
        // 使用真实玩家数据
        dataToRender = props.weekly_total_player_win || [];
        break;
      
      default:
        dataToRender = [];
    }
    
    // Get payment type label for display
    let paymentTypeLabel = '';
    if (state.paymentType === 'prepaid') {
      paymentTypeLabel = '儲值制';
    } else if (state.paymentType === 'credit') {
      paymentTypeLabel = '信用制';
    }

    // Function to render the sort icon based on current sort state
    const renderSortIcon = (field) => {
      if (state.sortField !== field) {
        return <i className="fa fa-sort text-muted ml-1" style={{ opacity: 0.5 }}></i>;
      } else if (state.sortDirection === 'desc') {
        return <i className="fa fa-sort-amount-desc text-primary ml-1"></i>;
      } else {
        return <i className="fa fa-sort-amount-asc text-primary ml-1"></i>;
      }
    };

    return (
      <Card className="shadow-lg border-0 entity-table-card">
        <CardHeader className="bg-gradient-primary text-white border-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Button 
                color="light" 
                size="sm" 
                className="mr-3 rounded-circle back-button" 
                onClick={navigateBack}
                disabled={state.navigationHistory.length === 0}
              >
                <i className="fa fa-arrow-left"></i>
              </Button>
              <CardTitle style={{fontWeight:"600", display: 'inline-block', fontSize:"18px"}} tag="h5" className="mb-0 ml-1">
                <span style={{fontSize:"18px"}}>{title}</span>
                <span> {state.periodSelectionMode === 'multi' ? '多周總和' : '單周記錄'} </span>
                {state.periodSelectionMode !== 'multi' && usePeriods && usePeriods[props.currentPeriodIndex] && (
                  <span className="text-white ml-2" style={{fontSize:"14px"}}>
                    {formatDate(usePeriods[props.currentPeriodIndex])}
                  </span>
                )}
                <Badge color="light" className="ml-2 text-dark px-2 rounded-pill" style={{fontSize:"12px"}}>
                  總數: {(state.showPlayerTable || state.hierarchyLevel === 'player') ? (props.weekly_total_player_count || 0) : dataToRender.length}
            </Badge>
            {state.paymentType !== 'all' && (
                  <Badge color="warning" className="ml-2 px-2 rounded-pill" style={{fontSize:"12px"}}>
                {paymentTypeLabel}
              </Badge>
            )}
          </CardTitle>
            </div>
          </div>
          
          {/* Breadcrumb navigation */}
          {renderBreadcrumbs()}
        </CardHeader>
        <CardBody className="p-0">
          <div 
            className="table-wrapper"
            style={{ 
              position: 'relative',
              height: '750px',
              backgroundColor: '#1a1a1a',
              width: '100%',
              minWidth: '1200px' // 固定最小宽度，确保玩家和代理表格宽度一致
            }}
          >
            <div className="table-scroll" style={{ width: '100%' }}>
            <Table hover className="mb-0 align-items-center table-flush" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead className="thead-light" style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 2,
                  backgroundColor: '#1a1a1a'
                }}>
                <tr>
                  <th className="text-center" style={{ width: '50px' }}>#</th>
                  
                  {/* Render different headers based on hierarchy level */}
                  {state.hierarchyLevel === 'union' && (
                    <>
                      <th style={{width: '100px'}}>聯盟 ID</th>
                      <th style={{width: '200px'}}>聯盟名稱</th>
                    </>
                  )}
                  
                  {state.hierarchyLevel === 'club' && (
                    <>
                      <th style={{width: '100px'}}>俱樂部 ID</th>
                      <th style={{width: '200px'}}>俱樂部名稱</th>
                    </>
                  )}
                  
                  {state.hierarchyLevel === 'agent' && !state.showPlayerTable && (
                    <>
                      <th style={{width: '100px'}}>代理 ID</th>
                      <th style={{width: '200px'}}>代理名稱</th>
                      <th style={{ cursor: 'pointer', width: '120px' }} onClick={() => handleSort('agentType')}>
                        <div className="d-flex align-items-center">
                          類型 {renderSortIcon('agentType')}
                        </div>
                      </th>
                    </>
                  )}
                  
                  {state.hierarchyLevel === 'subagent' && !state.showPlayerTable && (
                    <>
                      <th style={{width: '100px'}}>子代理 ID</th>
                      <th style={{width: '200px'}}>子代理名稱</th>
                      {/* 为子代理表格添加类型列 */}
                      <th style={{ width: '120px' }}>
                        類型
                      </th>
                    </>
                  )}
                  
                  {(state.hierarchyLevel === 'player' || state.showPlayerTable) && (
                    <>
                      <th style={{width: '100px'}}>玩家ID</th>
                      <th style={{width: '200px'}}>玩家名稱</th>
                      <th style={{ width: '120px' }}>
                        <div className="d-flex align-items-center">
                          類型
                        </div>
                      </th>
                    </>
                  )}
                  
                  {/* Common columns for all hierarchy levels with sort buttons */}
                  {!state.showPlayerTable ? (
                                        // 只有聯盟層或9527聯盟裡的俱樂部或9527俱樂部裡的代理層才顯示服務費%（子代理通通不顯示）
                     (state.hierarchyLevel === 'union' || 
                      (state.hierarchyLevel === 'club' && state.currentUnionId === '9527') ||
                      (state.hierarchyLevel === 'agent' && state.currentClubId === '9527')) ? (
                      <th className="text-right" style={{width: '100px'}}>
                        服務費%
                      </th>
                    ) : (
                      <th style={{ width: '100px', padding: 0, border: 'none' }}></th>
                    )
                    ) :(
                    <th style={{ width: '100px', padding: 0, border: 'none' }}></th>
                    )
                  }
                  <th className="text-right" onClick={() => handleSort('serviceFee')} style={{ cursor: 'pointer', width: '120px' }}>
                    <div className="d-flex justify-content-end align-items-center">
                      總服務費 {renderSortIcon('serviceFee')}
                    </div>
                  </th>
                  <th className="text-right" onClick={() => handleSort('totalWin')} style={{ cursor: 'pointer', width: '120px' }}>
                    <div className="d-flex justify-content-end align-items-center">
                      總輸贏 {renderSortIcon('totalWin')}
                    </div>
                  </th>
                  <th className="text-right" onClick={() => handleSort('leaderboard')} style={{ cursor: 'pointer', width: '150px' }}>
                    <div className="d-flex justify-content-end align-items-center">
                      LEADERBOARD {renderSortIcon('leaderboard')}
                    </div>
                  </th>
                  {!state.showPlayerTable ? (
                                        // 只有聯盟層或9527聯盟裡的俱樂部或9527俱樂部裡的代理層才顯示總交付（子代理通通不顯示）
                     (state.hierarchyLevel === 'union' || 
                      (state.hierarchyLevel === 'club' && state.currentUnionId === '9527') ||
                      (state.hierarchyLevel === 'agent' && state.currentClubId === '9527')) ? (
                      <th className="text-right" onClick={() => handleSort('delivery')} style={{ cursor: 'pointer', width: '120px' }}>
                        <div className="d-flex justify-content-end align-items-center">
                          總交付 {renderSortIcon('delivery')}
                        </div>
                      </th>
                    ) : (
                      <th style={{ width: '120px', padding: 0, border: 'none' }}></th>
                    )
                  ) : (
                    /* 添加一个隐藏的列，保持表格宽度一致 */
                    <th style={{ width: '120px', padding: 0, border: 'none' }}></th>
                  )}
                  <th className="text-center" style={{width: '80px'}}>
                    {!state.showPlayerTable && '操作'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataToRender.length > 0 ? (
                  dataToRender.map((entity, index) => {
                    // Extract the appropriate data based on hierarchy level
                    let id, name, serviceFeePercent, totalServiceFee, totalWin, leaderboardRank, totalDelivery, isSuperAgent;
                    
                    switch (state.hierarchyLevel) {
                      case 'union':
                        id = entity.unionId;
                        name = entity.unionName;
                        serviceFeePercent = entity.serviceFeePercent || '3.0%';
                        totalServiceFee = entity.totalServiceFee;
                        totalWin = entity.totalWin;
                        leaderboardRank = entity.leaderboardRank;
                        totalDelivery = entity.totalDelivery;
                        break;
                        
                      case 'club':
                        id = entity.clubId;
                        name = entity.clubName;
                        serviceFeePercent = entity.serviceFeePercent;
                        totalServiceFee = entity.totalServiceFee;
                        totalWin = entity.totalWin;
                        leaderboardRank = entity.leaderboardRank;
                        totalDelivery = entity.totalDelivery;
                        break;
                        
                      case 'agent':
                        if (state.showPlayerTable) {
                          id = entity.playerId;
                          name = entity.playerName;
                          serviceFeePercent = '';
                          totalServiceFee = entity.totalServiceFee;
                          totalWin = entity.totalWin;
                          leaderboardRank = entity.leaderboardRank;
                          totalDelivery = ''; // 不显示总交付
                        } else {
                        id = entity.agentId;
                        name = entity.agentName;
                        serviceFeePercent = entity.serviceFeePercent;
                        totalServiceFee = entity.totalServiceFee;
                        totalWin = entity.totalWin;
                        leaderboardRank = entity.leaderboardRank;
                        totalDelivery = entity.totalDelivery;
                        isSuperAgent = entity.isSuperAgent;
                        }
                        break;
                        
                      case 'subagent':
                        if (state.showPlayerTable) {
                          // 在超代层级切换到玩家表格 = 显示超代直客
                          id = entity.playerId;
                          name = entity.playerName;
                          serviceFeePercent = '';
                          totalServiceFee = entity.totalServiceFee;
                          totalWin = entity.totalWin;
                          leaderboardRank = entity.leaderboardRank;
                          totalDelivery = ''; // 不显示总交付
                        } else {
                          // 修复字段名称：API返回的是小写的subagentId和subagentName
                          id = entity.subagentId || entity.subAgentId;
                          name = entity.subagentName || entity.subAgentName;
                          // 子代理通通不需要顯示服務費%和總交付
                          serviceFeePercent = '';
                          totalServiceFee = entity.totalServiceFee;
                          totalWin = entity.totalWin;
                          leaderboardRank = entity.leaderboardRank;
                          totalDelivery = '';
                        }
                        break;
                      case 'player':
                        id = entity.playerId;
                        name = entity.playerName;
                        serviceFeePercent = ''; // 服务费百分比为空
                        totalServiceFee = entity.totalServiceFee;
                        totalWin = entity.totalWin;
                        leaderboardRank = entity.leaderboardRank; // 优先使用leaderRank
                        totalDelivery = ''; // 不显示总交付
                        break;
                    }
                    // Check if this entity has payment type info available
                    let paymentTypeInfo = '';
                    let isValueStore = false;
                    
                    try {
                      if (state.hierarchyLevel === 'union' || state.hierarchyLevel === 'club' || 
                          state.hierarchyLevel === 'agent' || state.hierarchyLevel === 'subagent') {
                        // Use storeValue directly from the entity data (from backend API)
                        // All levels now have storeValue field from backend
                        isValueStore = entity.storeValue || false;
                        paymentTypeInfo = isValueStore ? '儲值制' : '信用制';
                      } else if (state.hierarchyLevel === 'player') {
                        const playerInfo = props.player_info && props.player_info.find(player => player.playerId === entity.playerId);
                        if (playerInfo) {
                          isValueStore = playerInfo.valueStore;
                          paymentTypeInfo = playerInfo.paymentType === 'prepaid' ? '儲值制' : '信用制';
                        }
                      }
                    } catch (e) {
                      console.error('Error getting payment type info:', e);
                    }
                    
                    // Calculate joined weeks warning for row highlighting
                    const actualJoinedWeeks = calculateActualJoinedWeeks(entity, selectedPeriods);
                    const shouldShowJoinedWeeksWarning = actualJoinedWeeks !== null && actualJoinedWeeks < selectedPeriods.length && state.periodSelectionMode === 'multi';
                    
                    return (
                        <tr key={id} 
                            style={{
                              transition: 'background-color 0.3s ease',
                              // animation: shouldHighlightRow(entity) ? 'highlightFade 2s' : 'none',
                              backgroundColor: shouldShowJoinedWeeksWarning 
                                ? 'rgba(220, 53, 69, 0.1)' // Red warning color for insufficient weeks (different from search highlight)
                                : shouldHighlightRow(entity) 
                                ? 'rgba(218, 139, 35, 0.32)' 
                                : 'rgba(33, 37, 41, 0.6)',
                              borderLeft: shouldShowJoinedWeeksWarning
                                ? '4px solid rgba(220, 53, 69, 0.6)' // Red warning border for insufficient weeks
                                : shouldHighlightRow(entity) 
                                ? '4px solid rgba(94, 114, 228, 0.8)' 
                                : '4px solid transparent',
                              width: '100%'
                            }}>
                        <td className="text-center font-weight-bold">{index + 1}</td>
                        <td className="font-weight-bold">{id}</td>
                        
                        {/* 玩家层级或切换到玩家表格时不显示圆形头像 */}
                        {(state.hierarchyLevel === 'player' || state.showPlayerTable) ? (
                        <td>
                          <div className="d-flex align-items-center">
                              <span className="font-weight-medium">{name}</span>
                            </div>
                          </td>
                        ) : (
                          <td>
                            <div className="d-flex align-items-center">
                              {!state.showPlayerTable && (
                            <div className="avatar-circle mr-2" style={{
                              backgroundColor: getColor(id, 0.2),
                              color: getColor(id),
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              border: `2px solid ${getColor(id, 0.6)}`
                            }}>
                                  {typeof name !== 'undefined' ? name.charAt(0) : '' }
                            </div>
                              )}
                            <div className="d-flex flex-wrap align-items-center">
                              <span className="font-weight-medium">{name}</span>
                              {(() => {
                                // Calculate actual joined weeks and show warning if needed
                                const actualJoinedWeeks = calculateActualJoinedWeeks(entity, selectedPeriods);
                                const shouldShowJoinedWeeksWarning = actualJoinedWeeks !== null && actualJoinedWeeks < selectedPeriods.length && state.periodSelectionMode === 'multi';
                                
                                return (
                                  <>
                                    {/* Show payment type badge first (on the left) */}
                                    {paymentTypeInfo && (
                                      <Badge style={{fontSize:"12px"}}
                                             color={isValueStore ? 'success' : 'info'} 
                                             className="ml-2" 
                                             pill
                                             size="sm">
                                        {paymentTypeInfo}
                                      </Badge>
                                    )}
                                    {/* Show joined weeks warning after payment type badge */}
                                    {shouldShowJoinedWeeksWarning && (
                                      <Badge style={{fontSize:"12px"}} 
                                             color="danger" 
                                             className="ml-2" 
                                             pill
                                             size="sm">
                                        僅加入{actualJoinedWeeks}周
                                      </Badge>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </td>
                        )}
                        
                        {/* Only render agent type column for agent level */}
                        {state.hierarchyLevel === 'agent' && (
                          <td>
                            {state.showPlayerTable ? (
                              <Badge style={{fontSize:"12px"}} color="success" className="rounded-pill px-3 py-2">
                                Player
                              </Badge>
                            ) : (
                            <Badge style={{fontSize:"12px"}} color={isSuperAgent ? 'warning' : 'info'} className="rounded-pill px-3 py-2">
                                {isSuperAgent ? 'Super Agent' : 'Normal Agent'}
                            </Badge>
                            )}
                          </td>
                        )}
                        
                        {/* 为player层级添加Player标签 */}
                        {state.hierarchyLevel === 'player' && (
                          <td>
                            <Badge style={{fontSize:"12px"}} color="success" className="rounded-pill px-3 py-2">
                              Player
                            </Badge>
                          </td>
                        )}
                        
                        {/* 为subagent层级添加类型列 */}
                        {state.hierarchyLevel === 'subagent' && (
                          <td>
                            {state.showPlayerTable ? (
                              <Badge style={{fontSize:"12px"}} color="success" className="rounded-pill px-3 py-2">
                                Player
                              </Badge>
                            ) : (
                              <Badge style={{fontSize:"12px"}} color="primary" className="rounded-pill px-3 py-2">
                                Sub Agent
                              </Badge>
                            )}
                          </td>
                        )}
                        
                        {/* 服務費%列 - 只有聯盟層或9527聯盟裡的俱樂部或9527俱樂部裡的代理層才顯示（子代理通通不顯示） */}
                        {!state.showPlayerTable ? (
                          (state.hierarchyLevel === 'union' || 
                           (state.hierarchyLevel === 'club' && state.currentUnionId === '9527') ||
                           (state.hierarchyLevel === 'agent' && state.currentClubId === '9527')) ? (
                            <td className="text-right font-weight-medium">{serviceFeePercent}</td>
                          ) : (
                            <td style={{ padding: 0 }}></td>
                          )
                        ) : (
                          <td style={{ padding: 0 }}></td>
                        )}
                        <td className="text-right">
                          <span className={totalServiceFee >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
                            {totalServiceFee >= 0 ? '+' : ''}{numberWithCommas(totalServiceFee)}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className={totalWin >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
                            {totalWin >= 0 ? '+' : ''}{numberWithCommas(totalWin)}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className={leaderboardRank >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
                            {leaderboardRank >= 0 ? '+' : ''}{numberWithCommas(leaderboardRank)}
                          </span>
                        </td>
                        {/* 總交付列 - 只有聯盟層或9527聯盟裡的俱樂部或9527俱樂部裡的代理層才顯示（子代理通通不顯示） */}
                        {!state.showPlayerTable ? (
                          (state.hierarchyLevel === 'union' || 
                           (state.hierarchyLevel === 'club' && state.currentUnionId === '9527') ||
                           (state.hierarchyLevel === 'agent' && state.currentClubId === '9527')) ? (
                            <td className="text-right">
                              <span className={totalDelivery >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>
                                {totalDelivery >= 0 ? '+' : ''}{numberWithCommas(totalDelivery)}
                              </span>
                            </td>
                          ) : (
                            <td style={{ padding: 0 }}></td>
                          )
                        ) : (
                          /* 添加一个隐藏的单元格，保持行宽度一致 */
                          <td style={{ padding: 0 }}></td>
                        )}
                        <td className="text-center">
                          {!state.showPlayerTable && (
                            <>
                          {/* Union level button navigates to Clubs */}
                          {state.hierarchyLevel === 'union' && (
                          <Button 
                              color="primary" 
                            size="sm" 
                              className="btn-icon rounded-circle"
                              onClick={() => loadClubsForUnionHandler(id)}
                          >
                              <i className="fa fa-chevron-right"></i>
                          </Button>
                          )}
                          
                          {/* Club level button navigates to Agents */}
                          {state.hierarchyLevel === 'club' && (
                            <Button 
                              color="primary" 
                              size="sm" 
                              className="btn-icon rounded-circle"
                              onClick={() => loadAgentsForClubHandler(id)}
                            >
                              <i className="fa fa-chevron-right"></i>
                            </Button>
                          )}
                          
                          {/* Only Super Agents can navigate to Sub Agents */}
                          {state.hierarchyLevel === 'agent' && isSuperAgent && (
                            <Button 
                              color="primary" 
                              size="sm" 
                              className="btn-icon rounded-circle"
                              onClick={() => loadSubagentsForAgentHandler(id)}
                            >
                              <i className="fa fa-chevron-right"></i>
                            </Button>
                          )}
                          
                          {/* Normal Agents navigate to their Players */}
                          {state.hierarchyLevel === 'agent' && !isSuperAgent && (
                            <Button 
                              color="primary" 
                              size="sm" 
                              className="btn-icon rounded-circle"
                              onClick={() => loadPlayersForAgentHandler(id)}
                            >
                              <i className="fa fa-chevron-right"></i>
                            </Button>
                          )}
                          
                          {/* Sub Agents navigate to their Players */}
                          {state.hierarchyLevel === 'subagent' && (
                            <Button 
                              color="primary" 
                              size="sm" 
                              className="btn-icon rounded-circle"
                              onClick={() => loadPlayersForAgentHandler(id)}
                            >
                              <i className="fa fa-chevron-right"></i>
                            </Button>
                          )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={
                      state.hierarchyLevel === 'agent' ? (state.showPlayerTable ? 7 : 8) :
                      state.hierarchyLevel === 'subagent' ? 8 : 
                      7
                    } className="text-center py-4">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <i className="fa fa-search fa-3x text-muted mb-3"></i>
                        <p className="mb-0 text-muted">沒有找到符合條件的資料</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
                <tfoot className="table-footer" style={{ 
                  position: 'sticky', 
                  bottom: 0, 
                  zIndex: 2,
                  backgroundColor: '#595959', 
                  borderTop: '2px solid #e9ecef',
                  width: '100%'
                }}>
                <tr style={{ width: '100%' }}>
                  <td colSpan={
                    state.hierarchyLevel === 'agent' ? '5' : 
                    state.hierarchyLevel === 'subagent' ? '5' : 
                    state.hierarchyLevel === 'player' ? '5' :
                    '4'
                  } className="text-right"><strong>總計:</strong></td>
                  <td className="text-right">
                    <strong className={
                      (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                      ? (props.weekly_total_player_sum.fee_sum >= 0 ? 'text-success' : 'text-danger')
                      : (dataToRender.reduce((sum, entity) => sum + (entity.totalServiceFee || 0), 0) >= 0 ? 'text-success' : 'text-danger')
                    }>
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? (props.weekly_total_player_sum.fee_sum >= 0 ? '+' : '')
                        : (dataToRender.reduce((sum, entity) => sum + (entity.totalServiceFee || 0), 0) >= 0 ? '+' : '')
                      }
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? numberWithCommas(props.weekly_total_player_sum.fee_sum)
                        : numberWithCommas(dataToRender.reduce((sum, entity) => sum + (entity.totalServiceFee || 0), 0))
                      }
                    </strong>
                  </td>
                  <td className="text-right">
                    <strong className={
                      (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                      ? (props.weekly_total_player_sum.total_win_sum >= 0 ? 'text-success' : 'text-danger')
                      : (dataToRender.reduce((sum, entity) => sum + (entity.totalWin || 0), 0) >= 0 ? 'text-success' : 'text-danger')
                    }>
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? (props.weekly_total_player_sum.total_win_sum >= 0 ? '+' : '')
                        : (dataToRender.reduce((sum, entity) => sum + (entity.totalWin || 0), 0) >= 0 ? '+' : '')
                      }
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? numberWithCommas(props.weekly_total_player_sum.total_win_sum)
                        : numberWithCommas(dataToRender.reduce((sum, entity) => sum + (entity.totalWin || 0), 0))
                      }
                    </strong>
                  </td>
                  <td className="text-right">
                    <strong className={
                      (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                      ? (props.weekly_total_player_sum.leaderboard_sum >= 0 ? 'text-success' : 'text-danger')
                      : (dataToRender.reduce((sum, entity) => sum + (entity.leaderboardRank || 0), 0) >= 0 ? 'text-success' : 'text-danger')
                    }>
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? (props.weekly_total_player_sum.leaderboard_sum >= 0 ? '+' : '')
                        : (dataToRender.reduce((sum, entity) => sum + (entity.leaderboardRank || 0), 0) >= 0 ? '+' : '')
                      }
                      {
                        (state.showPlayerTable || state.hierarchyLevel === 'player') && props.weekly_total_player_sum
                        ? numberWithCommas(props.weekly_total_player_sum.leaderboard_sum)
                        : numberWithCommas(dataToRender.reduce((sum, entity) => sum + (entity.leaderboardRank || 0), 0))
                      }
                    </strong>
                  </td>
                  {/* 總交付總計列 - 只有聯盟層或9527聯盟裡的俱樂部或9527俱樂部裡的代理層才顯示（子代理通通不顯示） */}
                  {!state.showPlayerTable ? (
                    (state.hierarchyLevel === 'union' || 
                     (state.hierarchyLevel === 'club' && state.currentUnionId === '9527') ||
                     (state.hierarchyLevel === 'agent' && state.currentClubId === '9527')) ? (
                      <td className="text-right">
                        <strong className={
                          dataToRender.reduce((sum, entity) => sum + (entity.totalDelivery || 0), 0) >= 0 
                          ? 'text-success' 
                          : 'text-danger'
                        }>
                          {
                            dataToRender.reduce((sum, entity) => sum + (entity.totalDelivery || 0), 0) >= 0 
                            ? '+' 
                            : ''
                          }
                          {numberWithCommas(
                            dataToRender
                              .reduce((sum, entity) => sum + (entity.totalDelivery || 0), 0)
                          )}
                        </strong>
                      </td>
                    ) : (
                      <td style={{ padding: 0, border: 'none' }}></td>
                    )
                  ) : (
                    <td style={{ padding: 0, border: 'none' }}></td>
                  )}
                  <td></td>
                </tr>
              </tfoot>
            </Table>
            </div>
          </div>
          
          {/* 添加分页组件 - 只在显示玩家表格时渲染 */}
          {(state.showPlayerTable || state.hierarchyLevel === 'player') && renderPagination()}
        </CardBody>
      </Card>
    );
  };

  // Toggle between single and multi period selection
  const togglePeriodSelectionMode = () => {
    setState(prev => ({
      ...prev,
      periodSelectionMode: prev.periodSelectionMode === 'single' ? 'multi' : 'single',
      selectedStartPeriod: null,
      selectedEndPeriod: null,
      isSelectingEndWeek: false,
      // Reset hierarchy level to union
      hierarchyLevel: 'union',
      currentUnionId: null,
      currentClubId: null,
      currentAgentId: null,
      superAgentId: null,
      navigationHistory: [],
      // Reset to non-player table mode
      showPlayerTable: false,
      isFromSubagent: false,
      // Reset pagination and sort states
      currentPage: 1,
      playerSortBy: 'fee',
      playerSortOrder: -1,
      sortField: 'default',
      sortDirection: 'desc',
      //
      searchQuery: '',
      // Reset search state when switching period modes
      searchResults: null,
      showSearchResults: false,
      searchedPath: null,
      highlightEntity: null
    }));

    // If switching to multi mode, clear any existing multi-period data
    if (state.periodSelectionMode === 'single') {
      props.clearMultiPeriodData();
    }

    // Load data for union level
    const hierarchyPath = {
      hierarchyLevel: 'union',
      level: 'union',
      unionId: null,
      clubId: null,
      agentId: null
    };
    loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
  };

  // Function to toggle between agent and player tables
  const togglePlayerTableMode = () => {
    // Toggle the table mode
    const newMode = !state.showPlayerTable;
    
    // Update state
    setState(prev => ({
      ...prev,
      showPlayerTable: newMode,
      // 重置分页和排序状态
      currentPage: 1,
      playerSortBy: 'fee',
      playerSortOrder: -1,
      // 在玩家表格中，默认按总服务费降序排序
      sortField: newMode ? 'serviceFee' : 'default',
      sortDirection: 'desc'
    }));
    
    // If switching to player view, load player data
    const periodIndexToUse = props.currentPeriodIndex !== undefined ? props.currentPeriodIndex : useCurrentPeriodIndex;
    
    if (newMode) {
      // 切换到玩家表格时，根据当前层级构建正确的hierarchyPath
      let hierarchyPath;
      
      if (state.hierarchyLevel === 'agent') {
        // 在俱乐部层级（代理列表）切换到玩家表格 = 俱乐部直客
        hierarchyPath = {
          hierarchyLevel: 'player',
          level: 'player',
          unionId: state.currentUnionId,
          clubId: state.currentClubId,
          superAgentId: "",  // 俱乐部直客：空字符串
          agentId: ""        // 俱乐部直客：空字符串
        };
      } else if (state.hierarchyLevel === 'subagent') {
        // 在超代层级（子代理列表）切换到玩家表格 = 超代直客
        // 注意：由于setState是异步的，state.superAgentId可能还没更新，所以使用currentAgentId
        const superAgentIdToUse = state.superAgentId || state.currentAgentId;
        hierarchyPath = {
          hierarchyLevel: 'player',
          level: 'player',
          unionId: state.currentUnionId,
          clubId: state.currentClubId,
          superAgentId: superAgentIdToUse,  // 使用正确的超代ID
          agentId: ""                       // 超代直客：空字符串
        };
      } else {
        // 其他情况使用默认逻辑，但要确保传递正确的超代ID
        hierarchyPath = buildHierarchyPath('player', state.currentUnionId, state.currentClubId, state.superAgentId, state.currentAgentId);
      }
      loadPeriodPlayerDataWithPath(periodIndexToUse, hierarchyPath);
    } else {
      // 切换回代理表格，使用loadPeriodData加载代理数据
      const hierarchyPath = buildHierarchyPath(state.hierarchyLevel);
      loadPeriodDataWithPath(periodIndexToUse, hierarchyPath);
    }
  };

  // Add these new functions for drag selection
  const handleWeekMouseDown = () => {};
  const handleWeekMouseEnter = () => {};
  const handleWeekMouseUp = () => {};

  // Add mouse up handler to document
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (state.isDragging) {
        setState(prev => ({
          ...prev,
          isDragging: false
        }));
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [state.isDragging]);

  // Render period selector dropdown
  const renderPeriodSelector = () => {
    if (!usePeriods || usePeriods.length === 0) {
      return null;
    }
    
    const renderWeekItem = (period, index) => {
      const isSelected = state.periodSelectionMode === 'multi' && 
        state.selectedStartPeriod !== null && 
        state.selectedEndPeriod !== null && 
        index >= Math.min(state.selectedStartPeriod, state.selectedEndPeriod) && 
        index <= Math.max(state.selectedStartPeriod, state.selectedEndPeriod);

      const formattedDate = formatDate(period);
      const [startDate, endDate] = formattedDate.split(' - ');
      
      const handleClick = () => {
        setState(prev => {
          // If no selection has started, or if we're starting over
          if (prev.selectedStartPeriod === null || prev.isSelectingEndWeek === false) {
            return {
              ...prev,
              selectedStartPeriod: index,
              selectedEndPeriod: index,
              isSelectingEndWeek: true // Add this flag to track selection state
            };
          }
          
          // If we're selecting the end week
          if (prev.isSelectingEndWeek) {
            const start = Math.min(prev.selectedStartPeriod, index);
            const end = Math.max(prev.selectedStartPeriod, index);
            return {
              ...prev,
              selectedEndPeriod: index,
              isSelectingEndWeek: false // Reset the flag after end week is selected
            };
          }
          
          return prev;
        });
      };

      return (
        <div
          key={index}
          className={`week-item ${isSelected ? 'selected' : ''}`}
          onClick={handleClick}
          style={{
            padding: '4px 1px',
            cursor: 'pointer',
            backgroundColor: isSelected ? '#4CAF50' : '#383838',
            color: 'white',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '42px',
            position: 'relative',
            boxShadow: isSelected ? '0 2px 8px rgba(76, 175, 80, 0.3)' : '0 1px 3px rgba(0,0,0,0.2)',
            border: isSelected ? '1px solid #81C784' : '1px solid #4A4A4A',
            width: '100%'
          }}
        >
          <div style={{ 
            fontSize: '0.65rem',
            color: isSelected ? '#E8F5E9' : '#CCC',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            padding: '0 1px'
          }}>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
              {startDate}
            </span>
            <div style={{ 
              width: '8px',
              height: '1px',
              backgroundColor: isSelected ? '#A5D6A7' : '#555',
              margin: '1px 0'
            }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
              {endDate}
            </span>
          </div>
        </div>
      );
    };

    return (
      <div className="week-selector-container">
        <div className="week-picker-header mb-2 d-flex justify-content-between align-items-center">
          <h5 className="text-light mb-0">
            <i className="fa fa-calendar mr-2"></i>選擇週期
          </h5>
          <div className="mode-switch d-flex align-items-center">
            <span className="text-light mr-2" style={{ fontSize: '0.8rem' }}>多周總和</span>
            <div 
              className="custom-switch"
              onClick={togglePeriodSelectionMode}
              style={{
                width: '40px',
                height: '20px',
                backgroundColor: state.periodSelectionMode === 'multi' ? '#4CAF50' : '#666',
                borderRadius: '10px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: state.periodSelectionMode === 'multi' ? '22px' : '2px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              />
            </div>
          </div>
        </div>

        {state.periodSelectionMode === 'single' ? (
          <div className="week-picker-controls d-flex align-items-center" style={{width:"50%"}}>
            <div className="week-picker-select flex-grow-1 mr-3">
              <Input 
                type="select" 
                className="form-control text-white border-secondary custom-select"
                value={String(useCurrentPeriodIndex)}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value);
                  if (newIndex >= 0 && newIndex < usePeriods.length) {
                    const hierarchyPath = buildPlayerHierarchyPath();
                    
                    // Check if we're in player table mode to call the appropriate API
                    if (state.showPlayerTable) {
                      loadPeriodPlayerDataWithPath(newIndex, hierarchyPath);
                    } else {
                      loadPeriodDataWithPath(newIndex, hierarchyPath);
                    }
                  }
                }}
                style={{
                  height: '38px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  fontWeight: '500',
                  backgroundColor: '#505050'
                }}
              >
                {usePeriods.map((period, index) => (
                  <option 
                    key={index} 
                    value={index.toString()} 
                    style={{
                      backgroundColor: '#505050', 
                      color: 'white',
                      fontWeight: '500'
                    }}
                  >
                    {formatDate(period)}
                  </option>
                ))}
              </Input>
            </div>
            
            <div className="week-navigation-buttons">
              <Button 
                color="primary" 
                outline
                size="sm" 
                className="mr-2" 
                onClick={() => navigateToPeriod(1)} 
                disabled={useCurrentPeriodIndex >= usePeriods.length - 1}
                style={{
                  width: '100px',
                  borderRadius: '20px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fa fa-chevron-left mr-1"></i> 上一週
              </Button>
              <Button 
                color="primary"
                outline
                size="sm" 
                onClick={() => navigateToPeriod(-1)} 
                disabled={useCurrentPeriodIndex <= 0}
                style={{
                  width: '100px',
                  borderRadius: '20px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                下一週 <i className="fa fa-chevron-right ml-1"></i>
              </Button>
            </div>
          </div>
        ) : (
          <div className="multi-week-selector">
            <div>
              <div 
                className="weeks-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: '#2A2A2A',
                  borderRadius: '8px',
                  maxHeight: '240px', // Reduced from 320px
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
                  width: '100%'
                }}
              >
                {usePeriods.map((period, index) => renderWeekItem(period, index))}
              </div>
              
              {/* Selection Info */}
              {state.selectedStartPeriod !== null && (
                <div 
                  className="selected-range-info mt-3"
                  style={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    padding: '8px 12px',
                    fontSize: '0.85rem'
                  }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      backgroundColor: '#4CAF50',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px'
                    }}>
                      <i className="fa fa-calendar text-white" style={{ fontSize: '10px' }}></i>
                    </div>
                    <span className="text-white">已選擇週期:</span>
                  </div>
                  <div className="d-flex flex-column text-white ml-4">
                    <div className="mb-1">
                      <span className="mr-2">起始週期:</span>
                      <strong style={{ color: '#81C784' }}>
                        {formatDate(usePeriods[state.selectedStartPeriod])}
                      </strong>
                    </div>
                    {state.selectedEndPeriod !== state.selectedStartPeriod && (
                      <div>
                        <span className="mr-2">結束週期:</span>
                        <strong style={{ color: '#81C784' }}>
                          {formatDate(usePeriods[state.selectedEndPeriod])}
                        </strong>
                      </div>
                    )}
                    {state.isSelectingEndWeek && state.selectedStartPeriod !== null && (
                      <div style={{ color: '#FFB74D' }}>
                        <i className="fa fa-info-circle mr-1"></i>
                        請選擇結束週期
                      </div>
                    )}
                    {!state.isSelectingEndWeek && state.selectedStartPeriod !== null && (
                      <div className='mt-2'>
                        已選擇 
                        <strong className="ml-1 mr-1" style={{ color: '#81C784' }}>{Math.abs(state.selectedEndPeriod - state.selectedStartPeriod) + 1}</strong> 
                        週
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              {state.selectedStartPeriod !== null && (
                <div className="d-flex justify-content-end mt-3">
                  <Button 
                    color="secondary" 
                    size="sm" 
                    className="mr-2"
                    onClick={handleReset}
                    style={{
                      background: 'linear-gradient(45deg, #6c757d, #495057)',
                      border: '1px solid #495057',
                      borderRadius: '4px',
                      padding: '4px 12px',
                      fontWeight: '500',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      minWidth: '60px',
                      fontSize: '0.75rem',
                      height: '28px',
                      lineHeight: '1'
                    }}
                  >
                    <i className="fa fa-refresh mr-1"></i>
                    重置
                  </Button>
                  <Button 
                    color="success" 
                    size="sm"
                    onClick={handleSubmit}
                    disabled={state.selectedEndPeriod === null || state.selectedEndPeriod === state.selectedStartPeriod}
                    style={{
                      background: 'linear-gradient(45deg, #4CAF50, #81C784)',
                      border: '1px solid #43A047',
                      borderRadius: '4px',
                      padding: '4px 12px',
                      fontWeight: '500',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                      minWidth: '60px',
                      opacity: (state.selectedEndPeriod === null || state.selectedEndPeriod === state.selectedStartPeriod) ? '0.7' : '1',
                      color: '#fff',
                      fontSize: '0.75rem',
                      height: '28px',
                      lineHeight: '1'
                    }}
                  >
                    <i className="fa fa-check mr-1"></i>
                    送出
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Add submit handler
  const handleSubmit = () => {
    if (state.selectedStartPeriod !== null && state.selectedEndPeriod !== null) {
      const start = Math.min(state.selectedStartPeriod, state.selectedEndPeriod);
      const end = Math.max(state.selectedStartPeriod, state.selectedEndPeriod);
      const hierarchyPath = buildHierarchyPath(state.hierarchyLevel);
      
      // Check if we're in player table mode to call the appropriate API
      if (state.showPlayerTable) {
        // Call multi-period player data API
        if (loadMultiPeriodPlayerData) {
          loadMultiPeriodPlayerData(
            [start, end], 
            hierarchyPath, 
            state.playerSortBy, 
            state.playerSortOrder, 
            state.currentPage, 
            state.pageSize
          );
        }
      } else {
        // Call regular multi-period data API
        loadMultiPeriodData([start, end], hierarchyPath);
      }
    }
  };

  // Render time range selector dropdown
  const renderTimeRangeSelector = () => {
    const timeRanges = [
      { value: 'month', label: '最近一月' },
      { value: 'quarter', label: '最近一季' },
      { value: 'year', label: '最近一年' },
      { value: 'all', label: '全部時間' }
    ];

  return (
      <div className="mr-3 d-flex align-items-center">
        <span className="text-white mr-2">時間區間: </span>
        <Input 
          type="select" 
          className="form-control text-white border-secondary"
          value={state.timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          style={{
            height: '34px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            fontWeight: '500',
            background: 'linear-gradient(45deg, #8e44ad, #3498db)',
            border: '1px solid #9b59b6',
            width: '130px'
          }}
        >
          {timeRanges.map(range => (
            <option key={range.value} value={range.value} style={{ backgroundColor: '#673ab7', color: 'white' }}>
              {range.label}
            </option>
          ))}
        </Input>
      </div>
    );
  };
  
  // Render chart metric selector dropdown
  const renderChartMetricSelector = () => {
    const metrics = [
      { value: 'totalWin', label: '總輸贏' },
      { value: 'serviceFee', label: '總服務費' },
      { value: 'leaderboard', label: 'LeaderBoard' },
      { value: 'delivery', label: '總交付' }
    ];
    
    return (
      <div className="mr-3 d-flex align-items-center">
        <span className="text-white mr-2">圖表顯示數據: </span>
        <Input 
          type="select" 
          className="form-control text-white border-secondary"
          value={state.chartMetric}
          onChange={(e) => setChartMetric(e.target.value)}
          style={{
            height: '34px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            fontWeight: '500',
            background: 'linear-gradient(45deg, #8e44ad, #3498db)',
            border: '1px solid #9b59b6',
            width: '130px'
          }}
        >
          {metrics.map(metric => (
            <option key={metric.value} value={metric.value} style={{ backgroundColor: '#673ab7', color: 'white' }}>
              {metric.label}
            </option>
          ))}
        </Input>
            </div>
    );
  };
  
  // Render payment type filter
  const renderPaymentTypeFilter = () => {
    return (
      <ButtonGroup>
        <Button 
          color={state.paymentType === 'all' ? 'info' : 'secondary'} 
          onClick={() => setPaymentType('all')}
          className="px-3"
        >
          所有
        </Button>
        <Button 
          color={state.paymentType === 'prepaid' ? 'info' : 'secondary'} 
          onClick={() => setPaymentType('prepaid')}
          className="px-3"
        >
          儲值制
        </Button>
        <Button 
          color={state.paymentType === 'credit' ? 'info' : 'secondary'} 
          onClick={() => setPaymentType('credit')}
          className="px-3"
        >
          信用制
        </Button>
      </ButtonGroup>
    );
  };
  
  // Render breadcrumb navigation
  const renderBreadcrumbs = () => {
    const breadcrumbs = [];
    
    // Get entity data for names
    const currentData = state.periodSelectionMode === 'multi' ? 
      (props.weekly_total_win_sum || []) : 
      (props.weekly_total_win || []);
    
    // Debug logs
    // console.log('Current Data in breadcrumbs:', currentData);
    // console.log('Current IDs:', {
    //   unionId: state.currentUnionId,
    //   clubId: state.currentClubId,
    //   agentId: state.currentAgentId,
    //   isFromSubagent: state.isFromSubagent,
    //   superAgentId: state.superAgentId
    // });
    
    // Find the current union, club, agent names
    let unionName = '';
    let clubName = '';
    let agentName = '';
    let superAgentName = '';
    
    // Get names if IDs are available
    if (state.currentUnionId) {
      unionName = findEntityName(currentData, 'union', state.currentUnionId);
    }
    
    if (state.currentClubId) {
      clubName = findEntityName(currentData, 'club', state.currentClubId);
    }
    
    if (state.currentAgentId) {
      agentName = findEntityName(currentData, 'agent', state.currentAgentId);
    }
    
    if (state.superAgentId) {
      superAgentName = findEntityName(currentData, 'agent', state.superAgentId);
    }
    
    // Function to navigate to a specific level
    const navigateToBreadcrumb = (level, id = null) => {
      let newState = {
        hierarchyLevel: level,
        showPlayerTable: false // 确保返回时关闭玩家表格模式
      };
      
      let hierarchyPath = {
        hierarchyLevel: level,
        level: level
      };
      
      // Reset navigation history and set appropriate IDs based on level
      switch(level) {
        case 'union':
          newState = {
            ...newState,
            currentUnionId: null,
            currentClubId: null,
            currentAgentId: null,
            superAgentId: null,  // 清理超代ID，回到联盟层级时重置所有状态
            navigationHistory: []
          };
          hierarchyPath = {
            ...hierarchyPath,
            unionId: null,
            clubId: null,
            agentId: null
          };
          break;
          
        case 'club':
          newState = {
            ...newState,
            currentUnionId: id,
            currentClubId: null,
            currentAgentId: null,
            superAgentId: null,  // 清理超代ID，避免跨联盟导航时的状态污染
            navigationHistory: [{ level: 'club', id: id }]
          };
          hierarchyPath = {
            ...hierarchyPath,
            unionId: id,
            clubId: null,
            agentId: null
          };
          break;
          
        case 'agent':
          newState = {
            ...newState,
            // Keep currentUnionId from current state
            currentClubId: id,
            currentAgentId: null,
            superAgentId: null,  // 清理超代ID，避免跨俱乐部导航时的状态污染
            navigationHistory: [
              { level: 'club', id: state.currentUnionId },
              { level: 'agent', id: id }
            ]
          };
          hierarchyPath = {
            ...hierarchyPath,
            unionId: state.currentUnionId,
            clubId: id,
            agentId: null
          };
          break;
        
        case 'subagent':
          newState = {
            ...newState,
            // Keep currentUnionId and currentClubId from current state
            currentAgentId: id,
            navigationHistory: [
              { level: 'club', id: state.currentUnionId },
              { level: 'agent', id: state.currentClubId },
              { level: 'subagent', id: id }
            ]
          };
          hierarchyPath = {
            ...hierarchyPath,
            unionId: state.currentUnionId,
            clubId: state.currentClubId,
            agentId: id
          };
          break;
      }
      
      // Update state
      setState(prevState => ({
        ...prevState,
        ...newState
      }));
      
      // Load data with the updated hierarchy path
      loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
    };
    
    // Always add All Unions as the first breadcrumb
    breadcrumbs.push(
      <li key="union" className="breadcrumb-item">
        <span 
          className="breadcrumb-link"
          onClick={() => navigateToBreadcrumb('union')}
        >
          <i className="fa fa-home mr-1"></i>
          所有聯盟
        </span>
      </li>
    );
    
    // Add current union if available
    if (state.currentUnionId && ['club', 'agent', 'subagent', 'player'].includes(state.hierarchyLevel)) {
      breadcrumbs.push(
        <li key="union-specific" className="breadcrumb-item">
          <span 
            className="breadcrumb-link"
            onClick={() => navigateToBreadcrumb('club', state.currentUnionId)}
          >
              <i className="fa fa-building mr-1"></i>
              {unionName ? `${unionName} (${state.currentUnionId}聯盟)` : `聯盟 ${state.currentUnionId}`}
            </span>
        </li>
      );
    }
    
    // Add current club if available
    if (state.currentClubId && ['agent', 'subagent', 'player'].includes(state.hierarchyLevel)) {
      breadcrumbs.push(
        <li key="club" className="breadcrumb-item">
          <span 
            className="breadcrumb-link"
            onClick={() => navigateToBreadcrumb('agent', state.currentClubId)}
          >
              <i className="fa fa-users mr-1"></i>
              {clubName ? `${clubName} (${state.currentClubId}俱樂部)` : `俱樂部 ${state.currentClubId}`}
            </span>
        </li>
      );
    }
    

    
        // Add current agent if available for subagent level or player level
    if (state.currentAgentId && (state.hierarchyLevel === 'subagent' || state.hierarchyLevel === 'player')) {
      // 查找完整的代理信息
      let currentAgent = null;
      let displayAgentId = state.currentAgentId;
      let displayAgentName = agentName;
      
      if (state.hierarchyLevel === 'subagent') {
        // 在subagent層級，顯示的是超級代理信息（作為當前活動項目）
        displayAgentId = state.superAgentId || state.currentAgentId;
        
        // 重新查找超級代理名稱，使用與 title 相同的邏輯
        let currentSuperAgentName = '';
        if (displayAgentId) {
          // 首先從當前數據中查找
          currentSuperAgentName = findEntityName(currentData, 'agent', displayAgentId);
          
          // 如果沒找到，從代理層級數據中查找
          if (!currentSuperAgentName && props.weekly_total_win) {
            currentSuperAgentName = findEntityName(props.weekly_total_win, 'agent', displayAgentId);
          }
        }
        
        displayAgentName = currentSuperAgentName || agentName;
      } else if (state.hierarchyLevel === 'player') {
        // 在player層級，需要判斷是否來自子代理
        if (state.isFromSubagent && state.superAgentId) {
          // 先添加超級代理
          const superAgentName = findEntityName(currentData, 'agent', state.superAgentId) || '';
          breadcrumbs.push(
            <li key="superagent" className="breadcrumb-item">
              <span className="breadcrumb-link" 
                    onClick={() => navigateToBreadcrumb('subagent', state.superAgentId)}>
                <i className="fa fa-user-tie mr-1"></i>
                {superAgentName ? `${superAgentName} (${state.superAgentId}超級代理)` : `超級代理 ${state.superAgentId}`}
              </span>
            </li>
          );
        }
        
        // 尝试从props.weekly_total_win中查找代理信息
        currentAgent = props.weekly_total_win.find(e => e.agentId === state.currentAgentId);
        displayAgentName = currentAgent ? currentAgent.agentName : agentName;
      }
      
      // 根据上下文判断代理类型
      const agentType = (() => {
        if (state.hierarchyLevel === 'subagent') {
          return '超級代理';  // 在subagent層級，顯示的是超級代理
        } else if (state.isFromSubagent) {
          return '子代理';  // 從子代理進入的玩家層級
        } else {
          // 根据实际的代理类型确定
          if (currentAgent && currentAgent.isSuperAgent) {
            return '超級代理';
          } else {
            return '普通代理';
          }
        }
      })();
      
      const isActive = state.hierarchyLevel === 'subagent' || state.hierarchyLevel === 'player';
      
      breadcrumbs.push(
        <li key="agent" className={`breadcrumb-item ${isActive ? 'active' : ''}`}>
          <span className={isActive ? 'breadcrumb-active' : 'breadcrumb-link'}>
            <i className="fa fa-user mr-1"></i>
            {displayAgentName ? `${displayAgentName} (${displayAgentId}${agentType})` : `${agentType} ${displayAgentId}`}
          </span>
        </li>
      );
    }
    
    // 移除玩家层级的面包屑，不再显示"玩家"这一层
    // 只需要注释掉或删除这一段代码
    /*
    // Add player level for player hierarchy
    if (state.hierarchyLevel === 'player') {
      breadcrumbs.push(
        <li key="player" className="breadcrumb-item active">
          <span className="breadcrumb-active">
            <i className="fa fa-gamepad mr-1"></i>
            玩家
          </span>
        </li>
      );
    }
    */
    
    return (
      <nav aria-label="breadcrumb" className="mt-2">
        <ol className="breadcrumb custom-breadcrumb d-flex justify-content-between" style={{ 
          background: 'linear-gradient(87deg,rgb(8, 6, 87) 0,rgb(7, 51, 87) 100%)', 
          borderRadius: '8px', 
          padding: '10px 15px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
        }}>
          <div className="d-flex">
          {breadcrumbs}
          </div>
          
          {(state.hierarchyLevel === 'agent' || state.hierarchyLevel === 'subagent') && (
            <div className="d-flex align-items-center ml-auto">
              <span className="text-light mr-2" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>切換代理/玩家table</span>
              <div 
                className="custom-switch"
                onClick={togglePlayerTableMode}
                style={{
                  width: '40px',
                  height: '20px',
                  backgroundColor: state.showPlayerTable ? '#4CAF50' : '#666',
                  borderRadius: '10px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <div 
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: state.showPlayerTable ? '22px' : '2px',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                />
              </div>
            </div>
          )}
        </ol>
      </nav>
    );
  };
  
  // Add toggle function for chart visibility
  const toggleAllDatasets = () => {
    setState(prev => ({
      ...prev,
      chartDatasetVisibility: !prev.chartDatasetVisibility
    }));
  };
  
  // Render the chart view with the appropriate metric
  const renderChartView = () => {
    // Check if we have chart data
    if (!useChartData || !useChartData.labels || !useChartData.metrics) {
      return (
        <div className="chart-placeholder">
          <p>No chart data available. Please select a different view.</p>
        </div>
      );
    }

    // Generate the hierarchy path title
    const getHierarchyTitle = () => {
      const parts = [];
      if (state.hierarchyLevel === 'union') {
        return '所有聯盟';
      }
      if (state.currentUnionId) {
        parts.push(`聯盟: ${state.currentUnionId}`);
      }
      if (state.currentClubId && ['club', 'agent', 'subagent'].includes(state.hierarchyLevel)) {
        parts.push(`俱樂部: ${state.currentClubId}`);
      }
      if (state.currentAgentId && ['agent', 'subagent'].includes(state.hierarchyLevel)) {
        parts.push(`代理: ${state.currentAgentId}`);
      }
      return parts.join(' / ');
    };

    // Get level name in Chinese
    const getLevelName = () => {
      switch(state.hierarchyLevel) {
        case 'union': return '聯盟';
        case 'club': return '俱樂部';
        case 'agent': return '代理';
        case 'subagent': return '子代理';
        default: return '';
      }
    };
    
    // Map the frontend metric names to backend metric names
    const metricMapping = {
      'totalWin': 'totalWin',
      'serviceFee': 'totalServiceFee',
      'leaderboard': 'leaderboardRank',
      'delivery': 'totalDelivery'
    };
    
    // Get the backend metric name
    const backendMetric = metricMapping[state.chartMetric] || 'totalWin';
    
    // Get datasets for the selected metric
    let datasets = useChartData.metrics[backendMetric] || [];
    
    // Filter data by payment type if needed
    if (state.paymentType !== 'all') {
      const filteredEntities = new Set();
      
      // First, filter the current entities using the same function as the table view
      const entitiesForCurrentView = useWeeklyTotalWin || [];
      const filteredCurrentEntities = filterByPaymentType(entitiesForCurrentView, state.hierarchyLevel);
      
      // Create a set of IDs that passed the filter
      filteredCurrentEntities.forEach(entity => {
        const entityIdField = state.hierarchyLevel === 'union' ? 'unionId' : 
                             state.hierarchyLevel === 'club' ? 'clubId' : 
                             state.hierarchyLevel === 'agent' ? 'agentId' : 'subAgentId';
        
        if (entity[entityIdField]) {
          filteredEntities.add(entity[entityIdField]);
        }
      });
      
      // Now filter the chart datasets based on these filtered entity IDs
      datasets = datasets.filter(dataset => {
        return filteredEntities.has(dataset.entityId);
      });
    }
    
    // Apply visibility to datasets
    datasets = datasets.map(dataset => ({
      ...dataset,
      hidden: !state.chartDatasetVisibility
    }));
    
    // Chart configuration
    const chartData = {
      labels: useChartData.labels,
      datasets: datasets
    };
    
    // Chart options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)'
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)'
          }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            color: 'rgba(255, 255, 255, 0.7)',
            padding: 20,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.9)',
          bodyColor: 'rgba(255, 255, 255, 0.9)',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1
        }
      }
    };
    
    return (
      <div className="chart-wrapper mt-2">
        <div className="chart-title mb-3 p-3 bg-dark rounded shadow-sm">
          <div className="d-flex align-items-center">
            <div className="mr-3">
              <Badge className="px-3 py-2" style={{ fontSize: '0.9rem', backgroundColor: '#cc6600', color: 'white' }}>
                {getLevelName()}層級
              </Badge>
            </div>
            <div className="text-white" style={{ fontSize: '1rem' }}>
              <i className="fa fa-sitemap mr-2"></i>
              {getHierarchyTitle()}
            </div>
          </div>
        </div>
        <div className="chart-header d-flex justify-content-between align-items-center mb-2 ml-2">
          <Button
            color={state.chartDatasetVisibility ? "danger" : "success"}
            size="sm"
            onClick={toggleAllDatasets}
            className="px-3 py-2"
            style={{
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              background: state.chartDatasetVisibility 
                ? 'linear-gradient(45deg, #f5365c, #f56036)'
                : 'linear-gradient(45deg, #2dce89, #2dcecc)',
              border: 'none',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <i className={`fa fa-${state.chartDatasetVisibility ? 'eye-slash' : 'eye'}`}></i>
            {state.chartDatasetVisibility ? '隱藏全部' : '顯示全部'}
          </Button>
          <div className="d-flex align-items-center">
            {renderTimeRangeSelector()}
            {renderChartMetricSelector()}
          </div>
        </div>
        <div className="chart-container" style={{ 
          height: '500px', 
          padding: '15px',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '8px',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    );
  };
  
  // Set payment type filter
  const setPaymentType = (type) => {
    setState(prev => ({
      ...prev,
      paymentType: type
    }));
    
    // If in chart view, force a re-render
    if (state.viewMode === 'chart') {
      // Use setTimeout to ensure state is updated before rerender
      setTimeout(() => {
        // Just forcing a re-render by setting a dummy state update
        setState(prev => ({
          ...prev,
          lastUpdated: new Date().getTime()
        }));
      }, 0);
    }
  };
  
  // Set time range for chart - this needs to reload data from backend
  const setTimeRange = (range) => {
    // Update state
    setState(prev => ({
      ...prev,
      timeRange: range,
      timeRangeDropdownOpen: false
    }));
    
    // Pass the new range directly to loadChartDataHandler instead of using state
    // This ensures we use the selected range, not the previous state value
    setTimeout(() => {
      // Create a hierarchy path matching the current view
      const hierarchyPath = {
        hierarchyLevel: state.hierarchyLevel,
        level: state.hierarchyLevel,
        unionId: state.currentUnionId,
        clubId: state.currentClubId,
        agentId: state.currentAgentId
      };
      
      
      // Pass the hierarchy path and new time range to backend
      loadChartData(range, hierarchyPath);
    }, 0);
  };
  
  // Set chart metric - this only changes which metric is displayed
  const setChartMetric = (metric) => {
    setState(prev => ({
      ...prev,
      chartMetric: metric,
      chartMetricDropdownOpen: false
    }));
    
    // No need to reload data, just update the UI
    // The metric filtering happens in the renderChartView function
  };

  // Toggle between table and chart view modes
  const toggleViewMode = () => {
    const newMode = state.viewMode === 'table' ? 'chart' : 'table';
    
    setState(prev => ({
      ...prev,
      taxRatio: '',
      viewMode: newMode
    }));
    
    // Load chart data if switching to chart view
    if (newMode === 'chart') {
      // Use setTimeout to ensure state is updated before loading chart data
      setTimeout(() => loadChartDataHandler(), 0);
    }
  };

  // Render view mode toggle
  const renderViewModeToggle = () => {
    const buttonStyle = {
      fontSize: '0.875rem',
      padding: '0.5rem 1rem',
      minWidth: '90px',
      opacity: state.periodSelectionMode === 'multi' ? '0.5' : '1',
      cursor: state.periodSelectionMode === 'multi' ? 'not-allowed' : 'pointer'
    };
    
    return (
      <div className="view-mode-buttons">
        <ButtonGroup size="md">
                          <Button 
            color={state.viewMode === 'table' ? 'success' : 'secondary'}
            onClick={() => {
              if (state.viewMode !== 'table' && state.periodSelectionMode !== 'multi') {
                toggleViewMode();
              }
            }}
            className="px-3 py-2"
            style={buttonStyle}
            disabled={state.periodSelectionMode === 'multi'}
          >
            <i className="fa fa-table mr-1"></i>
            表格
                          </Button>
                          <Button 
            color={state.viewMode === 'chart' ? 'success' : 'secondary'}
            onClick={() => {
              if (state.viewMode !== 'chart' && state.periodSelectionMode !== 'multi') {
                toggleViewMode();
              }
            }}
            className="px-3 py-2"
            style={buttonStyle}
            disabled={state.periodSelectionMode === 'multi'}
          >
            <i className="fa fa-line-chart mr-1"></i>
            圖表
                          </Button>
                        </ButtonGroup>
                      </div>
    );
  };

  // Add renderSearchComponent function
  const renderSearchComponent = () => {
    return (
      <div className="mb-3 bg-dark rounded shadow-sm" style={{ width: '50%' }}>
        <div className="d-flex align-items-center">
          <div className="mr-3" style={{ minWidth: '120px' }}>
            <Input
              type="select"
              value={state.searchLevel}
              onChange={(e) => setState(prev => ({ ...prev, searchLevel: e.target.value }))}
              className="form-control text-white border-secondary"
              style={{
                height: '38px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                fontWeight: '500',
                background: 'linear-gradient(45deg, #8e44ad, #3498db)',
                border: '1px solid #9b59b6'
              }}
            >
              <option style={{ backgroundColor: '#673ab7', color: 'white' }} value="union">聯盟</option>
              <option style={{ backgroundColor: '#673ab7', color: 'white' }} value="club">俱樂部</option>
              <option style={{ backgroundColor: '#673ab7', color: 'white' }} value="agent">超級代理 / 普通代理</option>
              <option style={{ backgroundColor: '#673ab7', color: 'white' }} value="subagent">子代理</option>
            </Input>
          </div>
          <div className="flex-grow-1 mr-3">
            <Input
              type="text"
              className="form-control text-white border-secondary"
              placeholder="輸入ID或名稱搜尋..."
              value={state.searchQuery}
              onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
              style={{
                height: '38px',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                fontWeight: '500',
                background: 'linear-gradient(45deg, #8e44ad, #3498db)',
                border: '1px solid #9b59b6'
              }}
            />
          </div>
          <Button
            size="sm"
            className="d-flex align-items-center"
            style={{
              background: 'linear-gradient(45deg, #11cdef, #1171ef)',
              border: '1px solid #0099cc',
              borderRadius: '4px',
              padding: '6px 16px',
              fontWeight: '500',
              boxShadow: '0 2px 8px rgba(17, 205, 239, 0.3)',
              minWidth: '70px',
              color: '#fff',
              fontSize: '0.8rem',
              height: '32px',
              lineHeight: '1'
            }}
            disabled={state.searchQuery === ''}
            onClick={() => {
              if (props.onSearchEntity) {
                props.onSearchEntity(state.searchLevel, state.searchQuery);
              }
            }}
          >
            <i className="fa fa-search mr-1"></i>
            搜尋
          </Button>
        </div>
      </div>
    );
  };

  // Add this function to handle search result selection
  const handleSearchResultSelect = (entity) => {
    // Create a complete path object for matching
    const searchedPath = {
      level: entity.level,
      id: entity.id || (
        entity.level === 'union' ? entity.unionId :
        entity.level === 'club' ? entity.clubId :
        entity.level === 'agent' ? entity.agentId :
                                entity.subagentId || entity.subAgentId
      ),
      unionId: entity.unionId || null,
      clubId: entity.clubId || null,
      agentId: entity.agentId || null
    };


    // Clear search results and modal
    setState(prev => ({ 
      ...prev, 
      searchResults: null, 
      showSearchResults: false,
      hierarchyLevel: entity.level,
      currentUnionId: entity.unionId,
      currentClubId: entity.clubId,
      currentAgentId: entity.agentId,
      navigationHistory: buildNavigationHistory(entity),
      searchedPath  // Store the complete path
    }));

    // Build the correct hierarchy path
    const hierarchyPath = {
      hierarchyLevel: entity.level,
      level: entity.level,
      unionId: entity.unionId,
      clubId: entity.clubId,
      agentId: entity.agentId
    };

    // Load data based on period selection mode
    if (state.periodSelectionMode === 'multi' && 
        state.selectedStartPeriod !== null && 
        state.selectedEndPeriod !== null) {
      // For multi-period selection
      const periodRange = [state.selectedStartPeriod, state.selectedEndPeriod];
      if (props.loadMultiPeriodData) {
        props.loadMultiPeriodData(periodRange, hierarchyPath);
      }
    } else {
      // For single period selection
      loadPeriodDataWithPath(useCurrentPeriodIndex, hierarchyPath);
    }
  };

  // Helper function to build navigation history
  const buildNavigationHistory = (entity) => {
    const history = [];
    
    if (entity.unionId) {
      history.push({ level: 'club', id: entity.unionId });
    }
    
    if (entity.clubId) {
      history.push({ level: 'agent', id: entity.clubId });
    }
    
    if (entity.agentId) {
      history.push({ level: 'subagent', id: entity.agentId });
    }
    
    return history;
  };

  // Add this function to render search results modal
  const renderSearchResultsModal = () => {
    if (!state.searchResults || !state.showSearchResults) return null;

    return (
      <div 
        className="position-fixed"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div 
          className="bg-dark rounded p-4"
          style={{
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="text-white m-0">搜尋結果</h5>
            <button
              className="btn btn-link text-white"
              onClick={() => setState(prev => ({ ...prev, searchResults: null, showSearchResults: false }))}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
          <div className="list-group">
            {state.searchResults.map((result, index) => (
              <button
                key={`${result.id}-${index}`}
                className="list-group-item list-group-item-action bg-dark text-white border-secondary mb-2"
                onClick={() => handleSearchResultSelect(result)}
                style={{
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: '#1a1a1a',
                  padding: '12px'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="font-weight-bold">{result.name}</div>
                    <small className="text-muted">ID: {result.id}</small>
                  </div>
                  <div>
                    <span className="badge badge-primary mr-2">{result.level}</span>
                    {result.unionId && <span className="badge badge-secondary">Union: {result.unionId}</span>}
                    {result.clubId && <span className="badge badge-secondary ml-2">Club: {result.clubId}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Add useEffect to handle search results
  useEffect(() => {
    if (props.searchResults) {
      if (props.searchResults.length === 1) {
        // Single result - automatically select it
        handleSearchResultSelect(props.searchResults[0]);
      } else if (props.searchResults.length > 1) {
        // Multiple results - show selection UI
        setState(prev => ({
          ...prev,
          searchResults: props.searchResults,
          showSearchResults: true
        }));
      }
    }
  }, [props.searchResults]);

  // Update renderTabContent to include the search component
  const renderTabContent = () => {
    if (!useWeeklyTotalWin) {
      return null;
    }
    
    return (
      <React.Fragment>
        {/* Only show period selector and search in table view */}
        {state.viewMode === 'table' && (
          <>
            {renderPeriodSelector()}
            {renderSearchComponent()}
          </>
        )}
        
        <TabContent activeTab={state.activeTab} className="mt-3">
          <TabPane tabId={state.activeTab}>
            <Card className="card-chart">
              <CardBody className="p-0">
                {state.viewMode === 'table' ? renderEntityTable() : renderChartView()}
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </React.Fragment>
    );
  };
  
  // Dropdown toggle handlers
  const togglePaymentTypeDropdown = () => {
    setState(prev => ({ ...prev, isPaymentTypeOpen: !prev.isPaymentTypeOpen }));
  };
  
  const toggleTimeRangeDropdown = () => {
    setState(prev => ({ ...prev, isTimeRangeOpen: !prev.isTimeRangeOpen }));
  };
  
  const toggleChartMetricDropdown = () => {
    setState(prev => ({ ...prev, isChartMetricOpen: !prev.isChartMetricOpen }));
  };
  
  const togglePeriodSelectorDropdown = () => {
    setState(prev => ({ ...prev, isPeriodSelectorOpen: !prev.isPeriodSelectorOpen }));
  };
  
  // Update the reset handler to also reset the isSelectingEndWeek flag
  const handleReset = () => {
    setState(prev => ({
      ...prev,
      selectedStartPeriod: null,
      selectedEndPeriod: null,
      isSelectingEndWeek: false
    }));
    
    // 调用Redux action清空表格数据
    if (props.resetWeeklyTotalWinData) {
      props.resetWeeklyTotalWinData();
    }
  };
  
  // Add this helper function to check if a row should be highlighted
  const shouldHighlightRow = (entity) => {
    // Return false if no search path or entity
    if (!state.searchedPath || !entity) return false;

    // Get the current entity's path based on hierarchy level
    const entityId = (() => {
      switch (state.hierarchyLevel) {
        case 'union': return entity.unionId;
        case 'club': return entity.clubId;
        case 'agent': return entity.agentId;
        case 'subagent': return entity.subagentId || entity.subAgentId;
        default: return null;
      }
    })();

    // Return false if no entity ID found
    if (!entityId || !state.searchedPath.id) return false;

    const currentPath = {
      level: state.hierarchyLevel,
      id: entityId,
      unionId: state.hierarchyLevel === 'union' ? entity.unionId : state.currentUnionId,
      clubId: state.hierarchyLevel === 'club' ? entity.clubId : state.currentClubId,
      agentId: state.hierarchyLevel === 'agent' ? entity.agentId : state.currentAgentId
    };

    // Safely convert IDs to strings for comparison, with null checks
    const searchedId = String(state.searchedPath.id || '');
    const currentId = String(currentPath.id || '');
    const searchedUnionId = state.searchedPath.unionId ? String(state.searchedPath.unionId) : null;
    const currentUnionId = currentPath.unionId ? String(currentPath.unionId) : null;
    const searchedClubId = state.searchedPath.clubId ? String(state.searchedPath.clubId) : null;
    const currentClubId = currentPath.clubId ? String(currentPath.clubId) : null;
    const searchedAgentId = state.searchedPath.agentId ? String(state.searchedPath.agentId) : null;
    const currentAgentId = currentPath.agentId ? String(currentPath.agentId) : null;

    // Match based on hierarchy level with null checks
    const isMatch = 
      // Level must match
      state.searchedPath.level === currentPath.level &&
      // ID must match for the current level
      searchedId === currentId &&
      // For club level and below, unionId must match
      (state.searchedPath.level === 'union' || 
       !searchedUnionId || 
       searchedUnionId === currentUnionId) &&
      // For agent level and below, clubId must match
      (state.searchedPath.level === 'union' || 
       state.searchedPath.level === 'club' || 
       !searchedClubId || 
       searchedClubId === currentClubId) &&
      // For subagent level, agentId must match
      (state.searchedPath.level === 'union' || 
       state.searchedPath.level === 'club' || 
       state.searchedPath.level === 'agent' || 
       !searchedAgentId || 
       searchedAgentId === currentAgentId);

    // Debug log
    // console.log('Matching paths:', {
    //   searchedPath: state.searchedPath,
    //   currentPath,
    //   isMatch
    // });

    return isMatch;
  };
  
  // 处理分页变化
  const handlePageChange = (page) => {
    if (page !== state.currentPage) {
      setState(prevState => ({
        ...prevState,
        currentPage: page
      }));

      // 重新加载数据
      const periodIndexToUse = props.currentPeriodIndex !== undefined ? props.currentPeriodIndex : useCurrentPeriodIndex;
      const hierarchyPath = buildHierarchyPath(state.hierarchyLevel);
      
      if (loadPeriodPlayerData) {
        loadPeriodPlayerData(
          periodIndexToUse, 
          hierarchyPath, 
          state.playerSortBy,
          state.playerSortOrder,
          page,
          state.pageSize
        );
      }
    }
  };

  // 渲染分页组件
  const renderPagination = () => {
    const { currentPage, pageSize } = state;
    const totalItems = props.weekly_total_player_count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);
    
    // 如果没有数据或只有一页，不显示分页
    if (totalPages <= 1) return null;
    
    // 创建页码按钮
    const pageNumbers = [];
    const maxDisplayPages = 7; // 最多显示7个页码
    
    if (totalPages <= maxDisplayPages) {
      // 如果总页数少于最大显示页数，显示所有页码
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // 否则智能显示部分页码
      pageNumbers.push(1); // 始终显示第一页
      
      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);
      
      // 添加省略号
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // 添加中间的页码
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // 添加省略号
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      pageNumbers.push(totalPages); // 始终显示最后一页
    }
    
    return (
      <div className="d-flex align-items-center justify-content-between mb-3 mt-3 px-3">
        <div>
          <span style={{ color: '#ffffff', fontSize: '14px' }}>
            共 {totalItems} 位玩家，第 {currentPage}/{totalPages} 頁
          </span>
        </div>
        
        <div className="d-flex">
          <Button 
            size="sm" 
            color="primary"
            outline
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
            className="mr-1"
          >
            首頁
          </Button>
          
          <Button 
            size="sm" 
            color="primary"
            outline
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="mr-1"
          >
            上一頁
          </Button>
          
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <Button 
                  key={`ellipsis-${index}`}
                  size="sm" 
                  color="secondary"
                  outline
                  disabled
                  className="mr-1"
                >
                  {page}
                </Button>
              );
            }
            
            return (
              <Button 
                key={page}
                size="sm" 
                color={currentPage === page ? "info" : "primary"}
                outline={currentPage !== page}
                onClick={() => handlePageChange(page)}
                className="mr-1"
              >
                {page}
              </Button>
            );
          })}
          
          <Button 
            size="sm" 
            color="primary"
            outline
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="mr-1"
          >
            下一頁
          </Button>
          
          <Button 
            size="sm" 
            color="primary"
            outline
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            末頁
          </Button>
        </div>
      </div>
    );
  };
  
  // Main render function
  return (
    <div className="content">
      <Card className="shadow-lg border-0 mb-4 main-card">
        <CardHeader className="bg-gradient-dark text-white border-0 d-flex justify-content-between align-items-center">
          <CardTitle tag="h4" className="m-0 d-flex align-items-center">
            <div className="header-icon mr-3 d-flex align-items-center justify-content-center rounded-circle bg-primary"
              style={{
                width: '40px',
                height: '40px',
                boxShadow: '0 3px 6px rgba(0,0,0,0.16)'
              }}>
              <i className="fa fa-chart-line"></i>
                      </div>
            每周B端總輸贏分析
          </CardTitle>
          <div className="header-controls d-flex align-items-center mb-3">
            <div className="d-flex align-items-center">
              {/* Tax Ratio Filter */}
              {state.viewMode === 'table' && (
              <div className="mr-3 d-flex align-items-center">
                <span className="text-white mr-2">稅率比例: </span>
                
                <Input 
                  type="select"
                  className="form-control text-white border-secondary"
                  value={state.taxFilterType}
                  onChange={(e) => setState(prev => ({ ...prev, taxFilterType: e.target.value }))}
                  style={{
                    width: '200px',
                    height: '34px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    fontWeight: '500',
                    background: 'linear-gradient(45deg, #8e44ad, #3498db)',
                    border: '1px solid #9b59b6',
                  }}
                >
                  <option value="withLeaderboard" style={{ backgroundColor: '#673ab7', color: 'white' }}>總輸贏+LB/總服務費</option>
                  <option value="withoutLeaderboard" style={{ backgroundColor: '#673ab7', color: 'white' }}>總輸贏/總服務費</option>
                </Input>
                <span className="text-white mr-2 ml-2"> {">"} </span>
                <Input 
                  className="form-control text-white border-secondary"
                  type="number"
                  step="0.01"
                  placeholder="輸入比例"
                  value={state.taxRatio}
                  onChange={(e) => setState(prev => ({ ...prev, taxRatio: e.target.value }))}
                  style={{
                    width: '100px',
                    height: '34px',
                    marginRight: '10px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    fontWeight: '500',
                    background: 'linear-gradient(45deg, #8e44ad, #3498db)',
                  }}
                />
              </div>
              )}

              {/* Existing Payment Type Filter */}
              <div className="mr-3 d-flex align-items-center">
                <span className="text-white mr-2">B端支付方式: </span>
                <Input 
                  type="select" 
                  className="form-control text-white border-secondary"
                  value={state.paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  style={{
                    height: '34px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    fontWeight: '500',
                    background: 'linear-gradient(45deg, #8e44ad, #3498db)',
                    border: '1px solid #9b59b6',
                    width: '130px'
                  }}
                >
                  <option value="all" style={{ backgroundColor: '#673ab7', color: 'white' }}>所有</option>
                  <option value="prepaid" style={{ backgroundColor: '#673ab7', color: 'white' }}>儲值制</option>
                  <option value="credit" style={{ backgroundColor: '#673ab7', color: 'white' }}>信用制</option>
                </Input>
                </div>
              
              {/* Table/Chart Toggle */}
              <div>
                {renderViewModeToggle()}
            </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="bg-dark text-white p-0">
          {loading ? (
            <TinyLoading text="載入資料" variant="block" />
          ) : error ? (
            <div className="text-center py-5">
              <div className="mb-3 d-flex justify-content-center align-items-center">
                <i className="tim-icons icon-alert-circle-exc text-danger" style={{ fontSize: '3rem' }}></i>
              </div>
              <p className="text-danger">{error}</p>
              <Button color="primary" onClick={() => {
                setState(prev => ({
                  ...prev,
                  hierarchyLevel: 'union',
                  currentUnionId: null,
                  currentClubId: null,
                  currentAgentId: null,
                  navigationHistory: [],
                  selectedStartPeriod: null,
                  selectedEndPeriod: null,
                  isSelectingEndWeek: false
                }));
                
                // Clear multi-period data if in multi mode
                if (state.periodSelectionMode === 'multi') {
                  props.clearMultiPeriodData();
                }
                
                onLoad();
              }}>
                重試
              </Button>
            </div>
          ) : (
            <div className="p-4">
              {renderTabContent()}
            </div>
          )}
        </CardBody>
      </Card>
      
      <style>
        {`
          .entity-table-card {
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.2s ease;
          }
          
          .bg-gradient-primary {
            background: linear-gradient(87deg, #5e72e4 0, #825ee4 100%) !important;
          }
          
          .bg-gradient-dark {
            background: linear-gradient(87deg, #212529 0, #121212 100%) !important;
          }
          
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(0,0,0,0.05);
            border-radius: 3px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(94, 114, 228, 0.5);
            border-radius: 3px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(94, 114, 228, 0.8);
          }
          
          .custom-breadcrumb {
            margin-bottom: 0;
          }
          
          .custom-breadcrumb .breadcrumb-item {
            display: flex;
            align-items: center;
          }
          
          .breadcrumb-link {
            color: rgba(255,255,255,0.8);
            font-weight: 500;
            padding: 4px 8px;
            border-radius: 4px;
            transition: all 0.2s ease;
            text-decoration: none;
            cursor: pointer;
          }
          
          .breadcrumb-link:hover {
            color: white;
            background: rgba(255,255,255,0.1);
            text-decoration: none;
          }
          
          .breadcrumb-active {
            color: white;
            font-weight: 600;
          }
          
          .custom-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
            color: rgba(255,255,255,0.4);
          }
          
          .back-button {
            transition: all 0.2s ease;
            width: 32px;
            height: 32px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .back-button:hover {
            transform: translateX(-2px);
          }
          
          .payment-filter .btn {
            font-weight: 500;
            border-radius: 6px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            transition: all 0.2s ease;
          }
          
          .main-card {
            border-radius: 15px;
            overflow: hidden;
          }
          
          .header-icon {
            font-size: 1.2rem;
          }
          
          .table thead th {
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.5px;
          }
          
          .btn-icon {
            transition: all 0.2s;
          }
          
          .btn-icon:hover {
            transform: scale(1.1);
          }
          
          .avatar-circle {
            transition: all 0.2s ease;
          }
          
          tr:hover .avatar-circle {
            transform: scale(1.05);
          }
          
          .font-weight-medium {
            font-weight: 500;
          }
          
          .no-data-container {
            padding: 30px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .dropdown-menu {
            background-color: #2a2a2a;
            border: 1px solid rgba(255,255,255,0.1);
          }
          
          .dropdown-item {
            color: rgba(255,255,255,0.8);
          }
          
          .dropdown-item:hover {
            background-color: rgba(94, 114, 228, 0.1);
            color: white;
          }
          
          .dropdown-item.active {
            background-color: #5e72e4;
            color: white;
          }
        `}
      </style>
      <style>
        {`
          input::placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          input::-webkit-input-placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          input::-moz-placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          input:-ms-input-placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
          input:-moz-placeholder {
            color: rgba(255, 255, 255, 0.6) !important;
          }
        `}
      </style>
      {renderSearchResultsModal()}
      <style>
        {`
          @keyframes highlightFade {
            0% { background-color: rgba(94, 114, 228, 0.3); }
            100% { background-color: transparent; }
          }
          
          .table-primary {
            background-color: rgba(94, 114, 228, 0.1) !important;
          }
          
          .table-primary:hover {
            background-color: rgba(94, 114, 228, 0.2) !important;
          }
        `}
      </style>
      <style>
        {`
          .table-wrapper {
            border-radius: 8px;
            overflow: hidden;
          }

          .table-scroll {
            height: 100%;
            overflow-y: auto;
            overflow-x: hidden;
          }

          .table-scroll::-webkit-scrollbar {
            width: 6px;
          }

          .table-scroll::-webkit-scrollbar-track {
            background: rgba(0,0,0,0.05);
            border-radius: 3px;
          }

          .table-scroll::-webkit-scrollbar-thumb {
            background: rgba(94, 114, 228, 0.5);
            border-radius: 3px;
          }

          .table-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(94, 114, 228, 0.8);
          }

          .table-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(94, 114, 228, 0.5) rgba(0,0,0,0.05);
          }

          .table {
            margin-bottom: 0;
            background-color: transparent;
          }

          .table thead th,
          .table tbody td,
          .table tfoot td {
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1rem;
            vertical-align: middle;
          }

          .table tbody tr {
            transition: background-color 0.2s ease;
          }

          .table tbody tr:hover {
            background-color: rgba(255, 255, 255, 0.05);
          }

          .thead-light th {
            background-color: #1a1a1a;
            color: #ffffff;
            font-weight: 600;
            border-bottom: 2px solid rgba(255, 255, 255, 0.1);
          }

          .table-footer td {
            font-weight: 600;
            padding: 1rem;
          }
        `}
      </style>
    </div>
  );
};

export default WeeklyTotalWin;